import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import axios from 'axios';
import AdminSidebarRaw from '../Components/AdminSidebar';
import { API_BASE_URL } from '../config';
import '../Styles/Products.css';

const AdminSidebar = memo(AdminSidebarRaw);

const Products = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [subSubCategories, setSubSubCategories] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const [offers, setOffers] = useState([]);
    const [hiddenItems, setHiddenItems] = useState([]);


    // Filtering & Search
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState(null);
    const [filterSubCategory, setFilterSubCategory] = useState(null);
    const [filterSpecialty, setFilterSpecialty] = useState(null);
    const [filterSeason, setFilterSeason] = useState(null);
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [showHiddenOnly, setShowHiddenOnly] = useState(false);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        catID: '',
        subCatID: '',
        subSubID: '',
        StockQTY: '',
        Price: '',
        season: '',
        photo: null,
        type: '',
        can_preorder: 0,
        discount_type: '',
        discount_value: '',
        discount_start_date: '',
        discount_end_date: '',
        discount_is_active: 1,
        discount_id: null,
        size_guide_image: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    const sizeGuideEditInputRef = useRef(null);
    const [sizeGuidePreview, setSizeGuidePreview] = useState(null);
    const [sizeGuideViewerOpen, setSizeGuideViewerOpen] = useState(false);

    const [sizeGuideFile, setSizeGuideFile] = useState(null);
    const [sizeGuideModalPreview, setSizeGuideModalPreview] = useState(null);
    const sizeGuideInputRef = useRef(null);

    // Bulk Edit State
    const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isHideModalOpen, setIsHideModalOpen] = useState(false);
    const [bulkEditData, setBulkEditData] = useState({
        catID: '',
        subCatID: '',
        subSubID: '',
        season: ''
    });

    const [isQuickAddVisible, setIsQuickAddVisible] = useState(false);

    // Attributes State
    const [attributeValues, setAttributeValues] = useState([]);
    const [attributeFamilies, setAttributeFamilies] = useState([]);
    const [attrPickerOpen, setAttrPickerOpen] = useState(null); // null | 'Size' | 'Color' | 'Type'
    const [applyImageToAllColor, setApplyImageToAllColor] = useState(false);

    const nameRef = useRef(null);
    const catRef = useRef(null);
    const descRef = useRef(null);
    const priceRef = useRef(null);
    const stockRef = useRef(null);
    const seasonRef = useRef(null);

    useEffect(() => {
        if (isQuickAddVisible) {
            setTimeout(() => nameRef.current?.focus(), 100);
        }
    }, [isQuickAddVisible]);

    const handleQuickAddNav = (e, nextRef, prevRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
                if (nextRef === catRef) setIsCatPickerOpen(true);
            } else {
                submitQuickAdd();
            }
        } else if (e.key === 'ArrowRight') {
            if (nextRef && nextRef.current) {
                e.preventDefault();
                nextRef.current.focus();
            }
        } else if (e.key === 'ArrowLeft') {
            if (prevRef && prevRef.current) {
                e.preventDefault();
                prevRef.current.focus();
            }
        } else if (e.key === 'Escape') {
            setIsQuickAddVisible(false);
            setQuickAddData({
                name: '', description: '', catID: '', subCatID: '', subSubID: '',
                Price: '', StockQTY: '', season: '', type: '', photo: null, can_preorder: 0,
                discount_type: '', discount_value: '',
                discount_start_date: '', discount_end_date: '',
                discount_id: null
            });
            setQuickAddPreview(null);
        }
    };

    // Discount Modal State
    const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
    const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] = useState(false);
    const [isDiscountDeleteConfirmOpen, setIsDiscountDeleteConfirmOpen] = useState(false);
    const [discountContext, setDiscountContext] = useState(null); // 'quickAdd' | 'edit' | 'variationEdit'
    const [discountFormData, setDiscountFormData] = useState({
        discount_type: '',
        discount_value: '',
        discount_start_date: '',
        discount_end_date: '',
        discount_is_active: 1,
        discount_id: null
    });

    const handleOpenDiscountModal = (context, targetProduct = null) => {
        setDiscountContext(context);
        let source = {};
        if (context === 'quickAdd') source = quickAddData;
        else if (context === 'edit') source = formData;
        else if (context === 'variationEdit') source = variationFormData;
        else if (context === 'tableSingle') {
            source = targetProduct || products.find(p => p.id === selectedProducts[0]) || {};
        } else if (context === 'bulk' || context === 'bulkEdit') {
            source = {};
        }

        setDiscountFormData({
            discount_type: source.discount_type || '',
            discount_value: source.discount_value || '',
            discount_start_date: source.discount_start_date ? String(source.discount_start_date).substring(0, 10) : '',
            discount_end_date: source.discount_end_date ? String(source.discount_end_date).substring(0, 10) : '',
            discount_is_active: source.discount_is_active !== undefined ? source.discount_is_active : 1,
            discount_id: source.discount_id || null
        });
        setIsDiscountModalOpen(true);
    };

    const handleToggleDiscountStatus = () => {
        const newStatus = discountFormData.discount_is_active ? 0 : 1;
        if (newStatus === 0) {
            setIsDeactivateConfirmOpen(true);
        } else {
            setDiscountFormData(prev => ({ ...prev, discount_is_active: 1 }));
        }
    };

    const confirmDeactivation = () => {
        setDiscountFormData(prev => ({ ...prev, discount_is_active: 0 }));
        setIsDeactivateConfirmOpen(false);
    };

    const handleDeleteDiscountDirect = () => {
        setIsDiscountDeleteConfirmOpen(true);
    };

    const confirmDiscountDelete = async () => {
        setIsDiscountDeleteConfirmOpen(false);
        const adminToken = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${adminToken}` };

        try {
            if (discountContext === 'bulk' || discountContext === 'tableSingle' || discountContext === 'bulkEdit') {
                const targetIds = (discountContext === 'tableSingle')
                    ? [selectedProducts[0]]
                    : selectedProducts;

                // If single product in table and we have discount_id, delete directly
                const singleDiscountId = discountFormData.discount_id ||
                    (targetIds.length === 1 ? products.find(p => p.id === targetIds[0])?.discount_id : null);

                if (discountContext === 'tableSingle' && singleDiscountId) {
                    await axios.delete(`${API_BASE_URL}/admin/discounts/${singleDiscountId}`, { headers });
                } else {
                    try {
                        await axios.post(`${API_BASE_URL}/admin/discounts/bulk`, {
                            product_ids: targetIds,
                            discount_type: '',
                            discount_value: 0
                        }, { headers });
                    } catch (bulkErr) {
                        if (bulkErr.response && bulkErr.response.status === 404) {
                            // Fallback: delete discounts individually if bulk endpoint not yet deployed to host
                            const discToDelete = products
                                .filter(p => targetIds.includes(p.id) && p.discount_id)
                                .map(p => p.discount_id);

                            if (discToDelete.length > 0) {
                                await Promise.all(discToDelete.map(dId =>
                                    axios.delete(`${API_BASE_URL}/admin/discounts/${dId}`, { headers })
                                ));
                            } else {
                                const allDiscRes = await axios.get(`${API_BASE_URL}/admin/discounts`, { headers });
                                const matching = (allDiscRes.data || [])
                                    .filter(d => targetIds.includes(d.product_id) && !d.variation_id)
                                    .map(d => d.id);
                                if (matching.length > 0) {
                                    await Promise.all(matching.map(dId =>
                                        axios.delete(`${API_BASE_URL}/admin/discounts/${dId}`, { headers })
                                    ));
                                }
                            }
                        } else {
                            throw bulkErr;
                        }
                    }
                }

                setIsDiscountModalOpen(false);
                setDiscountContext(null);
                if (discountContext !== 'bulkEdit') {
                    setSelectedProducts([]);
                }
                fetchData();
                return;
            }

            if (discountFormData.discount_id) {
                await axios.delete(`${API_BASE_URL}/admin/discounts/${discountFormData.discount_id}`, { headers });
            }

            const resetData = {
                discount_type: '',
                discount_value: '',
                discount_start_date: '',
                discount_end_date: '',
                discount_is_active: 0,
                discount_id: null
            };

            if (discountContext === 'quickAdd') {
                setQuickAddData(prev => ({ ...prev, ...resetData }));
            } else if (discountContext === 'edit') {
                setFormData(prev => ({ ...prev, ...resetData }));
            } else if (discountContext === 'variationEdit') {
                setVariationFormData(prev => ({ ...prev, ...resetData }));
            }

            setIsDiscountModalOpen(false);
            setDiscountContext(null);
            fetchData();
            if (currentProductForVariations) fetchVariations(currentProductForVariations.id);

        } catch (error) {
            console.error('Error deleting discount:', error);
            alert('Failed to delete discount');
        }
    };

    const handleSaveDiscount = async () => {
        if (discountContext === 'bulk' || discountContext === 'tableSingle' || discountContext === 'bulkEdit') {
            try {
                const adminToken = localStorage.getItem('adminToken');
                const headers = { Authorization: `Bearer ${adminToken}` };
                const targetIds = (discountContext === 'tableSingle')
                    ? [selectedProducts[0]]
                    : selectedProducts;

                if (!targetIds || targetIds.length === 0) {
                    setIsDiscountModalOpen(false);
                    return;
                }

                try {
                    await axios.post(`${API_BASE_URL}/admin/discounts/bulk`, {
                        product_ids: targetIds,
                        discount_type: discountFormData.discount_type,
                        discount_value: discountFormData.discount_value,
                        discount_start_date: discountFormData.discount_start_date || null,
                        discount_end_date: discountFormData.discount_end_date || null,
                        discount_is_active: discountFormData.discount_is_active
                    }, { headers });
                } catch (bulkErr) {
                    if (bulkErr.response && bulkErr.response.status === 404) {
                        // Fallback: apply discount individually if bulk endpoint not yet deployed to host
                        for (const pid of targetIds) {
                            const prod = products.find(p => p.id === pid);
                            const payload = {
                                product_id: pid,
                                discount_type: discountFormData.discount_type,
                                discount_value: discountFormData.discount_value,
                                start_date: discountFormData.discount_start_date || null,
                                end_date: discountFormData.discount_end_date || null,
                                is_active: discountFormData.discount_is_active
                            };
                            if (prod && prod.discount_id) {
                                await axios.put(`${API_BASE_URL}/admin/discounts/${prod.discount_id}`, payload, { headers });
                            } else {
                                await axios.post(`${API_BASE_URL}/admin/discounts`, payload, { headers });
                            }
                        }
                    } else {
                        throw bulkErr;
                    }
                }

                setIsDiscountModalOpen(false);
                setDiscountContext(null);
                if (discountContext !== 'bulkEdit') {
                    setSelectedProducts([]);
                }
                fetchData();
            } catch (error) {
                console.error('Error applying bulk discount:', error);
                alert('Failed to apply discount to selected products.');
            }
            return;
        }

        if (discountContext === 'quickAdd') {
            setQuickAddData(prev => ({
                ...prev,
                ...discountFormData
            }));
        } else if (discountContext === 'edit') {
            setFormData(prev => ({
                ...prev,
                ...discountFormData
            }));
        } else if (discountContext === 'variationEdit') {
            setVariationFormData(prev => ({
                ...prev,
                ...discountFormData
            }));
        }
        setIsDiscountModalOpen(false);
        setDiscountContext(null);
    };

    const handleDiscountInputChange = (e) => {
        const { name, value } = e.target;
        setDiscountFormData(prev => ({ ...prev, [name]: value }));
    };

    // Variations State
    const [isVariationsModalOpen, setIsVariationsModalOpen] = useState(false);
    const [currentProductForVariations, setCurrentProductForVariations] = useState(null);
    const [variations, setVariations] = useState([]);
    const [variationFormData, setVariationFormData] = useState({
        Name: '',
        Size: '',
        SizeID: '',
        Color: '',
        ColorID: '',
        Type: '',
        TypeID: '',
        Qty: 0,
        photo: null,
        discount_type: '',
        discount_value: '',
        discount_start_date: '',
        discount_end_date: '',
        discount_is_active: 1,
        discount_id: null
    });
    const [variationPreview, setVariationPreview] = useState(null);
    const varFileInputRef = useRef(null);
    const [editingVariationId, setEditingVariationId] = useState(null);
    const [deletingVariationId, setDeletingVariationId] = useState(null);
    const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', title: 'Notice' });
    const [showColorDialog, setShowColorDialog] = useState(false);
    const [isSizeChartModalOpen, setIsSizeChartModalOpen] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [sizeScalesState, setSizeScalesState] = useState([]);

    // --- Offers Modal State ---
    const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
    const [isOfferStatusConfirmOpen, setIsOfferStatusConfirmOpen] = useState(false);
    const [pendingOfferStatus, setPendingOfferStatus] = useState(null);
    const [offerFormData, setOfferFormData] = useState({
        productID: '',
        offer_qty: '',
        offer_price: '',
        from_date: '',
        to_date: '',
        active: 1,
        group_id: ''
    });
    const [editingOfferId, setEditingOfferId] = useState(null);

    const handleOfferStatusToggle = (e) => {
        const newStatus = e.target.checked ? 1 : 0;
        setPendingOfferStatus(newStatus);
        setIsOfferStatusConfirmOpen(true);
    };

    const confirmOfferStatusChange = () => {
        setOfferFormData(prev => ({ ...prev, active: pendingOfferStatus }));
        setIsOfferStatusConfirmOpen(false);
        setPendingOfferStatus(null);
    };

    // --- Matrix Variation State ---
    const [matrixSizes, setMatrixSizes] = useState([]); // Selected size values
    const [matrixColors, setMatrixColors] = useState([]); // Selected color values
    const [matrixStock, setMatrixStock] = useState({}); // { 'Size-Color': qty }
    const [matrixColorPhotos, setMatrixColorPhotos] = useState({}); // { colorId: File }
    const [matrixColorPreviews, setMatrixColorPreviews] = useState({}); // { colorId: dataUrl }
    const [selectedMainColorId, setSelectedMainColorId] = useState(null);

    const showAlertDialog = (message, title = 'Notice') => {
        setAlertDialog({ isOpen: true, message, title });
    };

    // --- Matrix Handlers ---
    const toggleMatrixSize = (valObj) => {
        setMatrixSizes(prev => prev.find(s => s.id === valObj.id) ? prev.filter(s => s.id !== valObj.id) : [...prev, valObj]);
    };

    const toggleMatrixColor = (valObj) => {
        setMatrixColors(prev => prev.find(c => c.id === valObj.id) ? prev.filter(c => c.id !== valObj.id) : [...prev, valObj]);
    };

    const handleMatrixStockChange = (size, color, qty) => {
        setMatrixStock(prev => ({
            ...prev,
            [`${size}:${color}`]: qty
        }));
    };

    const handleColorPhotoChange = (colorId, file) => {
        if (!file) return;
        setMatrixColorPhotos(prev => ({ ...prev, [colorId]: file }));
        const reader = new FileReader();
        reader.onloadend = () => {
            setMatrixColorPreviews(prev => ({ ...prev, [colorId]: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    const getAttributeName = (idOrCode) => {
        if (idOrCode === null || idOrCode === undefined || idOrCode === '') return '—';
        const normalized = idOrCode.toString().trim();
        const input = normalized.toLowerCase();
        const attr = attributeValues.find(av => {
            const avId = av.id?.toString().trim();
            const avCode = (av.code || av.Code)?.toString().trim().toLowerCase();
            const avValue = (av.value || av.Value)?.toString().trim().toLowerCase();
            return avId === normalized || avCode === input || avValue === input;
        });
        if (!attr) return normalized;
        const valueField = attr.value || attr.Value;
        const codeField = attr.code || attr.Code;
        return valueField || codeField || normalized;
    };

    const handleMatrixKeyDown = (e, row, col) => {
        const numRows = matrixColors.length > 0 ? matrixColors.length : 1;
        const numCols = matrixSizes.length > 0 ? matrixSizes.length : 1;

        let nextRow = row;
        let nextCol = col;

        if (e.key === 'ArrowUp') {
            nextRow = Math.max(0, row - 1);
        } else if (e.key === 'ArrowDown') {
            nextRow = Math.min(numRows - 1, row + 1);
        } else if (e.key === 'ArrowLeft') {
            nextCol = Math.max(0, col - 1);
        } else if (e.key === 'ArrowRight') {
            nextCol = Math.min(numCols - 1, col + 1);
        } else if (e.key === 'Enter') {
            if (col < numCols - 1) {
                // Move right
                nextCol = col + 1;
            } else if (row < numRows - 1) {
                // Wrap to next row
                nextCol = 0;
                nextRow = row + 1;
            }
        } else {
            return;
        }

        if (nextRow !== row || nextCol !== col) {
            e.preventDefault();
            const nextInput = document.querySelector(`.matrix-input[data-row="${nextRow}"][data-col="${nextCol}"]`);
            if (nextInput) nextInput.focus();
        }
    };

    const submitMatrixVariations = async () => {
        if (!currentProductForVariations) return;

        // Allow even if both are empty? The user said "just one attribute", 
        // but if both are empty, it's just a general variation.
        if (matrixSizes.length === 0 && matrixColors.length === 0) {
            // We'll allow it, as it will just show a single 1x1 grid for a generic variation.
        }

        const variationsToCreate = [];
        const sizesToProcess = matrixSizes.length > 0 ? matrixSizes : [{ id: '', value: '' }];
        const colorsToProcess = matrixColors.length > 0 ? matrixColors : [{ id: '', value: '' }];

        sizesToProcess.forEach(sizeObj => {
            colorsToProcess.forEach(colorObj => {
                const key = `${sizeObj.id}:${colorObj.id}`;
                const qty = matrixStock[key];
                if (qty !== undefined && qty !== '' && parseInt(qty) >= 0) {
                    let label = currentProductForVariations.name;
                    if (sizeObj.value && colorObj.value) label += ` - ${sizeObj.value} / ${colorObj.value}`;
                    else if (sizeObj.value) label += ` - ${sizeObj.value}`;
                    else if (colorObj.value) label += ` - ${colorObj.value}`;

                    variationsToCreate.push({
                        SizeID: sizeObj.id || null,
                        ColorID: colorObj.id || null,
                        Qty: parseInt(qty),
                        Name: label,
                        Photo: matrixColorPhotos[colorObj.id] || null,
                        isMain: (selectedMainColorId !== null && selectedMainColorId === colorObj.id) ? 1 : 0
                    });
                }
            });
        });

        if (variationsToCreate.length === 0) {
            showAlertDialog('Please enter stock for at least one variation.', 'No Data');
            return;
        }

        try {
            await Promise.all(variationsToCreate.map(v => {
                const data = new FormData();
                data.append('ProdID', currentProductForVariations.id);
                data.append('Name', v.Name);
                data.append('Size', v.SizeID); // Posting ID as requested
                data.append('Color', v.ColorID); // Posting ID as requested
                data.append('Qty', v.Qty);
                if (v.Photo) data.append('photo', v.Photo);
                if (v.isMain) data.append('isMain', 1);

                // Inherit product discount for new variations if available
                if (currentProductForVariations.discount_type && currentProductForVariations.discount_value) {
                    data.append('discount_type', currentProductForVariations.discount_type);
                    data.append('discount_value', currentProductForVariations.discount_value);
                    data.append('discount_start_date', currentProductForVariations.discount_start_date || '');
                    data.append('discount_end_date', currentProductForVariations.discount_end_date || '');
                    data.append('discount_is_active', currentProductForVariations.discount_is_active !== undefined ? currentProductForVariations.discount_is_active : 1);
                }

                const adminToken = localStorage.getItem('adminToken');
                return axios.post(`${API_BASE_URL}/admin/variations`, data, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                });
            }));

            // Reset matrix
            setMatrixStock({});
            setMatrixSizes([]);
            setMatrixColors([]);
            setMatrixColorPhotos({});
            setMatrixColorPreviews({});
            setSelectedMainColorId(null);
            fetchVariations(currentProductForVariations.id);
            fetchData();
            showAlertDialog(`Successfully created ${variationsToCreate.length} variations.`, 'Success');

        } catch (error) {
            console.error('Error saving matrix variations:', error);
            showAlertDialog('Failed to save some variations. Please check console.', 'Error');
        }
    };

    // eslint-disable-next-line no-unused-vars
    const getSizeGroup = (sizeName) => {
        if (!sizeName) return '';
        const norm = sizeName.toString().trim().toUpperCase();

        // Group S-M
        if (norm === 'S' || norm === 'M' || norm === 'SMALL' || norm === 'MEDIUM' || norm === 'S-M' || norm === 'S - M') {
            return 'S-M';
        }

        // Group L-XL
        if (norm === 'L' || norm === 'XL' || norm === 'LARGE' || norm === 'EXTRA LARGE' || norm === 'L-XL' || norm === 'L - XL') {
            return 'L-XL';
        }

        // Group 2XL-3XL
        if (norm === '2XL' || norm === '3XL' || norm === 'XXL' || norm === 'XXXL' || norm === '2XL-3XL' || norm === '2XL - 3XL' || norm === 'XXL-XXXL') {
            return '2XL-3XL';
        }

        // Standalone / default
        return sizeName;
    };

    // eslint-disable-next-line no-unused-vars
    const sortSizeGroups = (groups) => {
        const order = {
            'xs': 1, 'extra small': 1,
            's-m': 2, 's - m': 2,
            'l-xl': 3, 'l - xl': 3,
            '2xl-3xl': 4, '2xl - 3xl': 4, 'xxl-xxxl': 4
        };
        return [...groups].sort((a, b) => {
            const aVal = a.name.toLowerCase();
            const bVal = b.name.toLowerCase();

            if (order[aVal] && order[bVal]) return order[aVal] - order[bVal];
            if (order[aVal]) return -1;
            if (order[bVal]) return 1;

            return aVal.localeCompare(bVal);
        });
    };

    const fetchSizeScales = async (prodId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/size-scales?product_id=${prodId}`);
            setSizeScalesState(response.data || []);
        } catch (err) {
            console.error('Error fetching size scales:', err);
        }
    };

    const handleSaveSizeGuideImage = async () => {
        if (!currentProductForVariations) return;
        const data = new FormData();

        data.append('name', currentProductForVariations.name);
        data.append('Price', currentProductForVariations.Price);
        if (currentProductForVariations.description) data.append('description', currentProductForVariations.description);
        if (currentProductForVariations.catID) data.append('catID', currentProductForVariations.catID);
        if (currentProductForVariations.subCatID) data.append('subCatID', currentProductForVariations.subCatID);
        if (currentProductForVariations.subSubID) data.append('subSubID', currentProductForVariations.subSubID);
        if (currentProductForVariations.season) data.append('season', currentProductForVariations.season);

        if (sizeGuideFile) {
            data.append('size_guide_image', sizeGuideFile);
        } else if (sizeGuideModalPreview === null) {
            data.append('size_guide_image', '');
        } else {
            data.append('size_guide_image', currentProductForVariations.size_guide_image || '');
        }

        try {
            const adminToken = localStorage.getItem('adminToken');
            const res = await axios.put(`${API_BASE_URL}/admin/products/${currentProductForVariations.id}`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${adminToken}`
                }
            });

            setProducts(prev => prev.map(p => p.id === currentProductForVariations.id ? { ...p, size_guide_image: res.data.size_guide_image } : p));
            setCurrentProductForVariations(prev => ({ ...prev, size_guide_image: res.data.size_guide_image }));

            setIsSizeChartModalOpen(false);
            setSizeGuideFile(null);
            setSizeGuideModalPreview(null);
            showAlertDialog('Size Guide Image updated successfully!', 'Success');
        } catch (error) {
            console.error('Error saving size guide image:', error);
            showAlertDialog('Failed to save size guide image', 'Error');
        }
    };

    useEffect(() => {
        if (isSizeChartModalOpen && currentProductForVariations) {
            fetchSizeScales(currentProductForVariations.id);
        }
    }, [isSizeChartModalOpen, currentProductForVariations]);


    // Auto-generate variation label from product name + attributes
    const autoVariationName = useMemo(() => {
        const base = currentProductForVariations?.name || '';
        const parts = [variationFormData.Size, variationFormData.Color, variationFormData.Type]
            .filter(Boolean);
        return parts.length > 0 ? `${base} - ${parts.join(' / ')}` : base;
    }, [currentProductForVariations, variationFormData.Size, variationFormData.Color, variationFormData.Type]);

    useEffect(() => {
        fetchData();
    }, []);
    const fetchData = async () => {
        const adminToken = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${adminToken}` };
        try {
            const [prodRes, catRes, subRes, subSubRes, seasonRes, attrValRes, attrFamRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/admin/products`, { headers }),
                axios.get(`${API_BASE_URL}/admin/categories`, { headers }),
                axios.get(`${API_BASE_URL}/admin/subcategories`, { headers }),
                axios.get(`${API_BASE_URL}/admin/subsubcategories`, { headers }),
                axios.get(`${API_BASE_URL}/admin/seasons`, { headers }),
                axios.get(`${API_BASE_URL}/admin/attribute-values`, { headers }),
                axios.get(`${API_BASE_URL}/admin/attribute-families`, { headers })
            ]);
            setProducts(prodRes.data);
            setCategories(catRes.data);
            setSubCategories(subRes.data);
            setSubSubCategories(subSubRes.data);
            setSeasons(seasonRes.data);
            setAttributeValues(attrValRes.data);
            setAttributeFamilies(attrFamRes.data);

            const offersRes = await axios.get(`${API_BASE_URL}/admin/offers`, { headers });
            setOffers(offersRes.data);

            const hiddenRes = await axios.get(`${API_BASE_URL}/admin/hidden`, { headers });
            setHiddenItems(hiddenRes.data);

        } catch (error) {
            console.error('Error fetching product data:', error);
        }
    };

    const filteredProducts = useMemo(() => {
        return products.filter(p => {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = p.name.toLowerCase().includes(searchLower) ||
                (p.description && p.description.toLowerCase().includes(searchLower)) ||
                (p.sku && p.sku.toLowerCase().includes(searchLower)) ||
                (p.Code && p.Code.toLowerCase().includes(searchLower));
            const matchesCat = !filterCategory || p.catID === filterCategory;
            const matchesSub = !filterSubCategory || p.subCatID === filterSubCategory;
            const matchesSpecialty = !filterSpecialty || p.subSubID === filterSpecialty;
            const matchesSeason = !filterSeason || String(p.season) === String(filterSeason);

            let matchesDateFrom = true;
            let matchesDateTo = true;

            if (p.Date) {
                const pDate = new Date(p.Date).setHours(0, 0, 0, 0);
                if (filterDateFrom) {
                    const fromDate = new Date(filterDateFrom).setHours(0, 0, 0, 0);
                    matchesDateFrom = pDate >= fromDate;
                }
                if (filterDateTo) {
                    const toDate = new Date(filterDateTo).setHours(0, 0, 0, 0);
                    matchesDateTo = pDate <= toDate;
                }
            } else if (filterDateFrom || filterDateTo) {
                matchesDateFrom = false;
                matchesDateTo = false;
            }

            const isHidden = hiddenItems.some(h => h.product_id === p.id && h.variation_id === null && h.isHidden === 1);

            if (showHiddenOnly) {
                if (!isHidden) return false;
            } else {
                if (isHidden) return false;
            }

            return matchesSearch && matchesCat && matchesSub && matchesSpecialty && matchesSeason && matchesDateFrom && matchesDateTo;
        });
    }, [products, searchTerm, filterCategory, filterSubCategory, filterSpecialty, filterSeason, filterDateFrom, filterDateTo, hiddenItems, showHiddenOnly]);

    const handleOpenModal = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name || '',
            description: product.description || '',
            catID: product.catID || '',
            subCatID: product.subCatID || '',
            subSubID: product.subSubID || '',
            StockQTY: product.StockQTY || '',
            Price: product.Price || '',
            season: product.season || '',
            photo: product.photo || null,
            type: product.type || '',
            can_preorder: product.can_preorder || 0,
            size_guide_image: product.size_guide_image || null,
            discount_type: product.discount_type || '',
            discount_value: product.discount_value || '',
            discount_start_date: product.discount_start_date ? product.discount_start_date.substring(0, 10) : '',
            discount_end_date: product.discount_end_date ? product.discount_end_date.substring(0, 10) : '',
            discount_is_active: product.discount_is_active !== undefined ? product.discount_is_active : 1,
            discount_id: product.discount_id || null
        });
        setImagePreview(product.varPhoto || product.photo ? `${API_BASE_URL}${product.varPhoto || product.photo}` : null);
        setSizeGuidePreview(product.size_guide_image ? `${API_BASE_URL}${product.size_guide_image}` : null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingProduct(null);
        setImagePreview(null);
        setSizeGuidePreview(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'catID' ? { subCatID: '', subSubID: '' } : {}),
            ...(name === 'subCatID' ? { subSubID: '' } : {})
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, photo: file }));
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSizeGuideFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, size_guide_image: file }));
            const reader = new FileReader();
            reader.onloadend = () => setSizeGuidePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSizeGuideModalFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSizeGuideFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setSizeGuideModalPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        Object.keys(formData).forEach(key => {
            if (formData[key] !== null && formData[key] !== undefined) {
                data.append(key, formData[key]);
            }
        });

        try {
            const adminToken = localStorage.getItem('adminToken');
            await axios.put(`${API_BASE_URL}/admin/products/${editingProduct.id}`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${adminToken}`
                }
            });
            fetchData();
            handleCloseModal();
        } catch (error) {
            console.error('Error updating product:', error);
            alert('Failed to update product.');
        }
    };

    const handleBulkEditChange = (e) => {
        const { name, value } = e.target;
        setBulkEditData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'catID' ? { subCatID: '', subSubID: '' } : {}),
            ...(name === 'subCatID' ? { subSubID: '' } : {})
        }));
    };

    const submitBulkEdit = async () => {
        try {
            const adminToken = localStorage.getItem('adminToken');
            await Promise.all(selectedProducts.map(async (id) => {
                const product = products.find(p => p.id === id);
                if (!product) return;

                const data = new FormData();
                data.append('name', product.name || '');
                data.append('description', product.description || '');
                data.append('StockQTY', product.StockQTY !== null && product.StockQTY !== undefined ? product.StockQTY : 0);
                data.append('Price', product.Price !== null && product.Price !== undefined ? product.Price : 0);

                const newCatID = bulkEditData.catID !== '' ? bulkEditData.catID : product.catID;
                const newSubCatID = bulkEditData.catID !== '' ? bulkEditData.subCatID : product.subCatID;
                const newSubSubID = bulkEditData.catID !== '' ? bulkEditData.subSubID : product.subSubID;
                const newSeason = bulkEditData.season !== '' ? bulkEditData.season : product.season;

                if (newCatID) data.append('catID', newCatID);
                if (newSubCatID) data.append('subCatID', newSubCatID);
                if (newSubSubID) data.append('subSubID', newSubSubID);
                if (newSeason) data.append('season', newSeason);

                if (product.photo) {
                    data.append('photo', product.photo);
                }

                await axios.put(`${API_BASE_URL}/admin/products/${id}`, data, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${adminToken}`
                    }
                });
            }));

            setIsBulkEditModalOpen(false);
            setBulkEditData({ catID: '', subCatID: '', subSubID: '', season: '' });
            setSelectedProducts([]);
            fetchData();
        } catch (error) {
            console.error('Error bulk updating:', error);
            alert('Failed to update some products.');
        }
    };


    // Quick Add State
    const [quickAddData, setQuickAddData] = useState({
        name: '',
        description: '',
        catID: '',
        subCatID: '',
        subSubID: '',
        Price: '',
        StockQTY: '',
        season: '',
        photo: null,
        type: '',
        can_preorder: 0,
        size_guide_image: null,
        discount_type: '',
        discount_value: '',
        discount_start_date: '',
        discount_end_date: '',
        discount_is_active: 1,
        discount_id: null
    });
    const [quickAddPreview, setQuickAddPreview] = useState(null);
    const quickAddFileRef = useRef(null);
    const quickAddSizeGuideFileRef = useRef(null);
    const [quickAddSizeGuidePreview, setQuickAddSizeGuidePreview] = useState(null);
    const [isCatPickerOpen, setIsCatPickerOpen] = useState(false);

    const handleQuickAddChange = (e) => {
        const { name, value } = e.target;
        setQuickAddData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'catID' ? { subCatID: '', subSubID: '' } : {}),
            ...(name === 'subCatID' ? { subSubID: '' } : {})
        }));
    };

    const handleQuickAddFile = (e) => {
        const file = e.target.files[0];
        if (file) {
            setQuickAddData(prev => ({ ...prev, photo: file }));
            const reader = new FileReader();
            reader.onloadend = () => setQuickAddPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleQuickAddSizeGuideFile = (e) => {
        const file = e.target.files[0];
        if (file) {
            setQuickAddData(prev => ({ ...prev, size_guide_image: file }));
            const reader = new FileReader();
            reader.onloadend = () => setQuickAddSizeGuidePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const submitQuickAdd = async () => {
        if (!quickAddData.name || !quickAddData.catID || !quickAddData.Price) {
            showAlertDialog('Please fill in essential fields: Name, Category, and Price.', 'Incomplete Information');
            return;
        }

        const data = new FormData();
        Object.keys(quickAddData).forEach(key => {
            if (quickAddData[key] !== null && quickAddData[key] !== undefined && quickAddData[key] !== '') {
                data.append(key, quickAddData[key]);
            }
        });

        try {
            const adminToken = localStorage.getItem('adminToken');
            await axios.post(`${API_BASE_URL}/admin/products`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${adminToken}`
                }
            });
            setQuickAddData({
                name: '',
                description: '',
                catID: '',
                subCatID: '',
                subSubID: '',
                Price: '',
                StockQTY: '',
                season: '',
                photo: null,
                type: '',
                can_preorder: 0,
                size_guide_image: null,
                discount_type: '',
                discount_value: '',
                discount_start_date: '',
                discount_end_date: '',
                discount_is_active: 1,
                discount_id: null
            });
            setQuickAddPreview(null);
            setQuickAddSizeGuidePreview(null);
            setIsQuickAddVisible(false);
            fetchData();
        } catch (error) {
            console.error('Error adding product:', error);
            alert('Failed to add product.');
        }
    };

    // Selection State
    const [selectedProducts, setSelectedProducts] = useState([]);

    const toggleSelectAll = () => {
        if (selectedProducts.length === filteredProducts.length) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(filteredProducts.map(p => p.id));
        }
    };

    const toggleSelectProduct = (id) => {
        setSelectedProducts(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleBulkDelete = () => {
        if (selectedProducts.length > 0) {
            setIsDeleteModalOpen(true);
        }
    };

    const confirmDelete = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            await Promise.all(selectedProducts.map(id => axios.delete(`${API_BASE_URL}/admin/products/${id}`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            })));
            setSelectedProducts([]);
            setIsDeleteModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error in bulk delete:', error);
            setIsDeleteModalOpen(false);
        }
    };

    const handleBulkToggleVisibility = () => {
        if (selectedProducts.length > 0) {
            setIsHideModalOpen(true);
        }
    };

    const confirmToggleVisibility = async () => {
        if (selectedProducts.length === 0) return;
        const adminToken = localStorage.getItem('adminToken');
        try {
            // Determine if we are hiding or unhiding based on the first selected item
            const firstItemHiddenRecord = hiddenItems.find(h => h.product_id === selectedProducts[0] && h.variation_id === null);
            const isCurrentlyHidden = firstItemHiddenRecord && firstItemHiddenRecord.isHidden === 1;

            await Promise.all(selectedProducts.map(async (id) => {
                const hiddenRecord = hiddenItems.find(h => h.product_id === id && h.variation_id === null);
                if (hiddenRecord) {
                    if (isCurrentlyHidden) {
                        // Unhide (delete the hidden record)
                        await axios.delete(`${API_BASE_URL}/admin/hidden/${hiddenRecord.id}`, { headers: { Authorization: `Bearer ${adminToken}` } });
                    } else {
                        // Hide
                        await axios.put(`${API_BASE_URL}/admin/hidden/${hiddenRecord.id}`, { product_id: id, isHidden: 1 }, { headers: { Authorization: `Bearer ${adminToken}` } });
                    }
                } else if (!isCurrentlyHidden) {
                    // Hide by creating a new record
                    await axios.post(`${API_BASE_URL}/admin/hidden`, { product_id: id, isHidden: 1 }, { headers: { Authorization: `Bearer ${adminToken}` } });
                }
            }));

            setSelectedProducts([]);
            setIsHideModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error in bulk visibility toggle:', error);
            alert('Failed to toggle visibility for some products.');
            setIsHideModalOpen(false);
        }
    };

    // --- Variation Handlers ---
    const handleOpenVariations = async (product) => {
        setCurrentProductForVariations(product);
        setIsVariationsModalOpen(true);
        setMatrixSizes([]);
        setMatrixColors([]);
        setMatrixStock({});
        fetchVariations(product.id);
    };

    const fetchVariations = async (prodID) => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const res = await axios.get(`${API_BASE_URL}/admin/variations?prodID=${prodID}`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setVariations(res.data);
        } catch (error) {
            console.error('Error fetching variations:', error);
        }
    };

    const handleVariationInputChange = (e) => {
        const { name, value } = e.target;
        setVariationFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleVariationFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setVariationFormData(prev => ({ ...prev, photo: file }));
            const reader = new FileReader();
            reader.onloadend = () => setVariationPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const submitVariation = async () => {
        if (!currentProductForVariations) return;

        const adminToken = localStorage.getItem('adminToken');
        const headers = {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${adminToken}`
        };

        try {
            if (editingVariationId && applyImageToAllColor && variationFormData.photo && variationFormData.ColorID) {
                const sameColorVars = variations.filter(v => v.Color === variationFormData.ColorID);
                for (const v of sameColorVars) {
                    const vData = new FormData();
                    vData.append('ProdID', currentProductForVariations.id);

                    if (v.varID === editingVariationId) {
                        vData.append('Name', variationFormData.Name || autoVariationName);
                        vData.append('Size', variationFormData.SizeID);
                        vData.append('Color', variationFormData.ColorID);
                        vData.append('Type', variationFormData.TypeID);
                        vData.append('Qty', variationFormData.Qty);
                        Object.keys(variationFormData).forEach(key => {
                            if (key.startsWith('discount_') && variationFormData[key] !== null && variationFormData[key] !== undefined && variationFormData[key] !== '') {
                                vData.append(key, variationFormData[key]);
                            }
                        });
                    } else {
                        vData.append('Name', v.Name || '');
                        vData.append('Size', v.Size || '');
                        vData.append('Color', v.Color || '');
                        vData.append('Type', v.Type || '');
                        vData.append('Qty', v.Qty || 0);
                        if (v.discount_type) vData.append('discount_type', v.discount_type);
                        if (v.discount_value) vData.append('discount_value', v.discount_value);
                        if (v.discount_start_date) vData.append('discount_start_date', v.discount_start_date.substring(0, 10));
                        if (v.discount_end_date) vData.append('discount_end_date', v.discount_end_date.substring(0, 10));
                        if (v.discount_is_active !== undefined) vData.append('discount_is_active', v.discount_is_active);
                        if (v.discount_id) vData.append('discount_id', v.discount_id);
                    }

                    vData.append('photo', variationFormData.photo);
                    await axios.put(`${API_BASE_URL}/admin/variations/${v.varID}`, vData, { headers });
                }
            } else {
                const data = new FormData();
                data.append('ProdID', currentProductForVariations.id);
                data.append('Name', variationFormData.Name || autoVariationName);
                data.append('Size', variationFormData.SizeID);
                data.append('Color', variationFormData.ColorID);
                data.append('Type', variationFormData.TypeID);
                data.append('Qty', variationFormData.Qty);
                if (variationFormData.photo) data.append('photo', variationFormData.photo);

                Object.keys(variationFormData).forEach(key => {
                    if (key.startsWith('discount_')) {
                        if (variationFormData[key] !== null && variationFormData[key] !== undefined && variationFormData[key] !== '') {
                            data.append(key, variationFormData[key]);
                        }
                    }
                });

                if (editingVariationId) {
                    await axios.put(`${API_BASE_URL}/admin/variations/${editingVariationId}`, data, { headers });
                } else {
                    await axios.post(`${API_BASE_URL}/admin/variations`, data, { headers });
                }
            }

            setVariationFormData({ Name: '', Size: '', SizeID: '', Color: '', ColorID: '', Type: '', TypeID: '', Qty: 0, photo: null, discount_id: null });
            setVariationPreview(null);
            setEditingVariationId(null);
            setApplyImageToAllColor(false);
            fetchVariations(currentProductForVariations.id);
            fetchData();

        } catch (error) {
            console.error('Error saving variation:', error);
            alert('Failed to save variation.');
        }
    };

    // --- Offers Handlers ---
    const handleOpenOfferModal = (product) => {
        if (!product && selectedProducts.length > 1) {
            setEditingOfferId(null);
            setOfferFormData({
                productID: 'multiple',
                offer_qty: '',
                offer_price: '',
                from_date: '',
                to_date: '',
                active: 1,
                group_id: 'grp_' + Date.now().toString().slice(-6)
            });
            setIsOfferModalOpen(true);
            return;
        }

        const existingOffer = offers.find(o => o.productID === product.id);
        if (existingOffer) {
            setEditingOfferId(existingOffer.id);
            setOfferFormData({
                productID: existingOffer.productID,
                offer_qty: existingOffer.offer_qty,
                offer_price: existingOffer.offer_price,
                from_date: existingOffer.from_date ? existingOffer.from_date.substring(0, 10) : '',
                to_date: existingOffer.to_date ? existingOffer.to_date.substring(0, 10) : '',
                active: existingOffer.active,
                group_id: existingOffer.group_id || ''
            });
        } else {
            setEditingOfferId(null);
            setOfferFormData({
                productID: product.id,
                offer_qty: '',
                offer_price: '',
                from_date: '',
                to_date: '',
                active: 1,
                group_id: ''
            });
        }
        setIsOfferModalOpen(true);
    };

    const handleSaveOffer = async (e) => {
        e.preventDefault();
        const adminToken = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${adminToken}` };

        try {
            if (offerFormData.productID === 'multiple') {
                await Promise.all(selectedProducts.map(async (id) => {
                    const existingOffer = offers.find(o => o.productID === id);
                    const data = { ...offerFormData, productID: id };
                    if (existingOffer) {
                        return axios.put(`${API_BASE_URL}/admin/offers/${existingOffer.id}`, data, { headers });
                    } else {
                        return axios.post(`${API_BASE_URL}/admin/offers`, data, { headers });
                    }
                }));
                setSelectedProducts([]);
            } else {
                if (editingOfferId) {
                    await axios.put(`${API_BASE_URL}/admin/offers/${editingOfferId}`, offerFormData, { headers });
                } else {
                    await axios.post(`${API_BASE_URL}/admin/offers`, offerFormData, { headers });
                }
            }
            fetchData();
            setIsOfferModalOpen(false);
        } catch (error) {
            console.error('Error saving offer:', error);
            alert('Failed to save offer');
        }
    };

    const handleDeleteOffer = async () => {
        if (!editingOfferId) return;
        const adminToken = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${adminToken}` };

        try {
            await axios.delete(`${API_BASE_URL}/admin/offers/${editingOfferId}`, { headers });
            fetchData();
            setIsOfferModalOpen(false);
        } catch (error) {
            console.error('Error deleting offer:', error);
            alert('Failed to delete offer');
        }
    };


    const handleEditVariation = (v) => {
        setEditingVariationId(v.varID);
        // If the server returns IDs in v.Size/Color, we resolve them to names for the UI
        const sizeName = getAttributeName(v.Size);
        const colorName = getAttributeName(v.Color);
        const typeName = getAttributeName(v.Type);

        setVariationFormData({
            Name: v.Name || '',
            Size: sizeName !== '—' ? sizeName : '',
            SizeID: v.Size || '',
            Color: colorName !== '—' ? colorName : '',
            ColorID: v.Color || '',
            Type: typeName !== '—' ? typeName : '',
            TypeID: v.Type || '',
            Qty: v.Qty || 0,
            photo: null,
            discount_type: v.discount_type || '',
            discount_value: v.discount_value || '',
            discount_start_date: v.discount_start_date ? v.discount_start_date.substring(0, 10) : '',
            discount_end_date: v.discount_end_date ? v.discount_end_date.substring(0, 10) : '',
            discount_is_active: v.discount_is_active !== undefined ? v.discount_is_active : 1,
            discount_id: v.discount_id || null
        });
        setVariationPreview(v.photo ? `${API_BASE_URL}${v.photo}` : null);
    };

    const handleDeleteVariation = async (varID) => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            await axios.delete(`${API_BASE_URL}/admin/variations/${varID}`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setDeletingVariationId(null);
            fetchVariations(currentProductForVariations.id);
            fetchData();
        } catch (error) {
            console.error('Error deleting variation:', error);
            alert('Failed to delete variation.');
        }
    };

    const handleSetMainVariation = async (v) => {
        const adminToken = localStorage.getItem('adminToken');
        const headers = { Authorization: `Bearer ${adminToken}` };
        try {
            if (v.isMain) {
                await axios.put(`${API_BASE_URL}/admin/variations/${v.varID}/unset-main`, {}, { headers });
            } else {
                await axios.put(`${API_BASE_URL}/admin/variations/${v.varID}/set-main`, {}, { headers });
            }
            fetchVariations(currentProductForVariations.id);
            fetchData();
        } catch (error) {
            console.error('Error updating main variation:', error);
            alert('Failed to update main variation status.');
        }
    };

    const handleSetMainColor = (colorId) => {
        const isCurrentlyMain = variations.some(v => v.Color === colorId && v.isMain);
        if (isCurrentlyMain) {
            const mainVar = variations.find(v => v.Color === colorId && v.isMain);
            if (mainVar) handleSetMainVariation(mainVar);
            return;
        }

        // Find the first variation with this color that has a photo
        const targetVar = variations.find(v => v.Color === colorId && v.photo);
        if (targetVar) {
            handleSetMainVariation(targetVar);
        } else {
            // Fallback to first variation of this color
            const firstVar = variations.find(v => v.Color === colorId);
            if (firstVar) handleSetMainVariation(firstVar);
        }
    };

    return (
        <div className="products-container">
            <AdminSidebar activePage="products" />

            <main className="products-main">
                <div className="products-search-box" style={{ maxWidth: '500px', width: '100%', backgroundColor: '#fff', height: '48px', marginTop: '-12px', marginBottom: '8px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', padding: '0 16px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ fontSize: '0.95rem', width: '100%', height: '100%', border: 'none', background: 'transparent', outline: 'none', color: 'var(--text-primary)', marginLeft: '8px' }}
                    />
                </div>
                <div className="products-controls" style={{ marginTop: '-12px' }}>
                    <div className="table-actions">
                        <div className="filter-item">
                            <select
                                value={filterCategory || ''}
                                onChange={(e) => {
                                    setFilterCategory(e.target.value ? parseInt(e.target.value) : null);
                                    setFilterSubCategory(null);
                                    setFilterSpecialty(null);
                                }}
                            >
                                <option value="">All Categories</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div className="filter-item">
                            <select
                                value={filterSubCategory || ''}
                                onChange={(e) => {
                                    setFilterSubCategory(e.target.value ? parseInt(e.target.value) : null);
                                    setFilterSpecialty(null);
                                }}
                                disabled={!filterCategory}
                            >
                                <option value="">All Sub-Categories</option>
                                {subCategories
                                    .filter(sc => sc.category_id === filterCategory)
                                    .map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                            </select>
                        </div>
                        <div className="filter-item">
                            <select
                                value={filterSpecialty || ''}
                                onChange={(e) => setFilterSpecialty(e.target.value ? parseInt(e.target.value) : null)}
                                disabled={!filterSubCategory}
                            >
                                <option value="">All Specialties</option>
                                {subSubCategories
                                    .filter(ssc => ssc.subcategory_id === filterSubCategory)
                                    .map(ssc => <option key={ssc.id} value={ssc.id}>{ssc.name}</option>)}
                            </select>
                        </div>
                        <div className="filter-item">
                            <select
                                value={filterSeason || ''}
                                onChange={(e) => setFilterSeason(e.target.value || null)}
                            >
                                <option value="">All Seasons</option>
                                {seasons.map(s => <option key={s.id} value={s.id}>{s.name} ({s.year})</option>)}
                            </select>
                        </div>
                        <div className="filter-item date-filter">
                            <label>From</label>
                            <input
                                type="date"
                                value={filterDateFrom}
                                onChange={(e) => setFilterDateFrom(e.target.value)}
                            />
                        </div>
                        <div className="filter-item date-filter">
                            <label>To</label>
                            <input
                                type="date"
                                value={filterDateTo}
                                onChange={(e) => setFilterDateTo(e.target.value)}
                            />
                        </div>

                        <button
                            className={`modern-toggle-btn ${showHiddenOnly ? 'active' : ''}`}
                            onClick={() => setShowHiddenOnly(!showHiddenOnly)}
                        >
                            <div className="toggle-icon-wrapper">
                                {showHiddenOnly ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                    </svg>
                                ) : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                        <circle cx="12" cy="12" r="3"></circle>
                                    </svg>
                                )}
                            </div>
                            <span>{showHiddenOnly ? 'Show Visible' : 'Show Hidden'}</span>
                        </button>

                        <button
                            className="modern-toggle-btn mobile-refresh-btn"
                            onClick={() => {
                                setSearchTerm('');
                                setFilterCategory(null);
                                setFilterSubCategory(null);
                                setFilterSpecialty(null);
                                setFilterSeason(null);
                                setFilterDateFrom('');
                                setFilterDateTo('');
                            }}
                            title="Reset filters"
                        >
                            <div className="toggle-icon-wrapper">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1 4 1 10 7 10"></polyline>
                                    <path d="M3.51 15a9 9 0 1 0 .49-4.5"></path>
                                </svg>
                            </div>
                        </button>
                    </div>
                </div>

                <div className="products-table-wrapper">
                    <table className="products-table">

                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}>
                                    <input
                                        type="checkbox"
                                        checked={filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length}
                                        onChange={toggleSelectAll}
                                        className="table-checkbox"
                                    />
                                </th>
                                <th>Photo</th>
                                <th style={{ minWidth: '120px' }}>Code</th>
                                <th style={{ minWidth: '150px' }}>Product</th>
                                <th>Description</th>
                                <th style={{ minWidth: '180px' }}>Categories</th>
                                <th style={{ minWidth: '120px' }}>Type</th>
                                <th style={{ minWidth: '140px' }}>Price</th>
                                <th style={{ minWidth: '120px' }}>Discount</th>
                                <th style={{ minWidth: '130px' }}>Stock</th>
                                <th style={{ minWidth: '90px' }}>Season</th>
                                <th style={{ minWidth: '100px' }}>Added</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Quick Add Row */}
                            {isQuickAddVisible && (
                                <tr className="quick-add-row" id="quick-add-row">
                                    <td></td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                                            <div className="product-img-mini quick-add-img" title="Product Photo" onClick={() => quickAddFileRef.current.click()}>
                                                {quickAddPreview ? (
                                                    <img src={quickAddPreview} alt="Preview" />
                                                ) : (
                                                    <div className="img-placeholder-add">+</div>
                                                )}
                                                <input type="file" ref={quickAddFileRef} onChange={handleQuickAddFile} style={{ display: 'none' }} accept="image/*" />
                                            </div>
                                            <div
                                                className="product-img-mini quick-add-img"
                                                title="Size Guide Image"
                                                onClick={() => quickAddSizeGuideFileRef.current.click()}
                                                style={{ width: '28px', height: '28px', border: '1px dashed #ccc', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f7' }}
                                            >
                                                {quickAddSizeGuidePreview ? (
                                                    <img src={quickAddSizeGuidePreview} alt="Size Guide Preview" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                                                ) : (
                                                    <div style={{ fontSize: '10px', lineHeight: '26px' }}>📐</div>
                                                )}
                                                <input type="file" ref={quickAddSizeGuideFileRef} onChange={handleQuickAddSizeGuideFile} style={{ display: 'none' }} accept="image/*" />
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>—</span>
                                    </td>
                                    <td>
                                        <div className="quick-add-inputs">
                                            <input
                                                type="text"
                                                name="name"
                                                ref={nameRef}
                                                placeholder="Name"
                                                className="quick-input name"
                                                value={quickAddData.name}
                                                onChange={handleQuickAddChange}
                                                onKeyDown={(e) => handleQuickAddNav(e, catRef, null)}
                                            />

                                        </div>
                                    </td>
                                    <td>
                                        <textarea
                                            name="description"
                                            ref={descRef}
                                            placeholder="Description"
                                            className="quick-input desc"
                                            value={quickAddData.description}
                                            onChange={handleQuickAddChange}
                                            onKeyDown={(e) => handleQuickAddNav(e, priceRef, catRef)}
                                            rows="2"
                                            style={{ minWidth: '200px' }}
                                        />

                                    </td>
                                    <td>
                                        <div
                                            className="quick-add-cat-trigger"
                                            ref={catRef}
                                            tabIndex="0"
                                            onClick={() => setIsCatPickerOpen(true)}
                                            onKeyDown={(e) => handleQuickAddNav(e, descRef, nameRef)}
                                        >

                                            {quickAddData.catID ? (
                                                <div className="cat-pill-group">
                                                    <span className="cat-pill main">{categories.find(c => c.id === parseInt(quickAddData.catID))?.name}</span>
                                                    {quickAddData.subCatID && (
                                                        <span className="cat-pill sub">{subCategories.find(sc => sc.id === parseInt(quickAddData.subCatID))?.name}</span>
                                                    )}
                                                    {quickAddData.subSubID && (
                                                        <span className="cat-pill specialty">{subSubCategories.find(ssc => ssc.id === parseInt(quickAddData.subSubID))?.name}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="select-placeholder">Select Categories</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <select
                                            name="type"
                                            className="quick-select"
                                            value={quickAddData.type}
                                            onChange={handleQuickAddChange}
                                            style={{ minWidth: '100px' }}
                                        >
                                            <option value="">Type</option>
                                            {attributeValues
                                                .filter(av => av.attributeName?.toLowerCase() === 'type')
                                                .map(av => (
                                                    <option key={av.id} value={av.id}>{av.value}</option>
                                                ))
                                            }
                                        </select>
                                    </td>
                                    <td>
                                        <div className="quick-price-box">
                                            <span>$</span>
                                            <input
                                                type="number"
                                                name="Price"
                                                ref={priceRef}
                                                placeholder="0.00"
                                                className="quick-input price"
                                                value={quickAddData.Price}
                                                onChange={handleQuickAddChange}
                                                onKeyDown={(e) => handleQuickAddNav(e, stockRef, descRef)}
                                            />

                                        </div>
                                    </td>
                                    <td>
                                        <button
                                            className={`quick-discount-trigger ${quickAddData.discount_type ? 'has-discount' : ''}`}
                                            onClick={() => handleOpenDiscountModal('quickAdd')}
                                        >
                                            {quickAddData.discount_type && quickAddData.discount_is_active ? (
                                                <div className="discount-summary">
                                                    <span className="type">{quickAddData.discount_type === 'percentage' ? '%' : '$'}</span>
                                                    <span className="val">{quickAddData.discount_value}</span>
                                                </div>
                                            ) : quickAddData.discount_type && !quickAddData.discount_is_active ? (
                                                <span className="placeholder paused">Paused</span>
                                            ) : (
                                                <span className="placeholder">Set Discount</span>
                                            )}
                                        </button>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            name="StockQTY"
                                            ref={stockRef}
                                            placeholder="Stock"
                                            className="quick-input stock"
                                            value={quickAddData.StockQTY}
                                            onChange={handleQuickAddChange}
                                            onKeyDown={(e) => handleQuickAddNav(e, seasonRef, priceRef)}
                                        />

                                    </td>
                                    <td>
                                        <div className="quick-add-season-actions">
                                            <select
                                                name="season"
                                                ref={seasonRef}
                                                className="quick-select"
                                                value={quickAddData.season}
                                                onChange={handleQuickAddChange}
                                                onKeyDown={(e) => handleQuickAddNav(e, null, stockRef)}
                                            >

                                                <option value="">Season</option>
                                                {seasons.map(s => <option key={s.id} value={s.id}>{s.name} ({s.year})</option>)}
                                            </select>
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', padding: '0 10px' }}>Now</span>
                                    </td>
                                </tr>
                            )}

                            {filteredProducts.length > 0 ? (
                                filteredProducts.map(product => (
                                    <tr key={product.id} className={`${selectedProducts.includes(product.id) ? 'selected-row' : ''} ${hiddenItems.some(h => h.product_id === product.id && h.isHidden === 1 && h.variation_id === null) ? 'hidden-row' : ''}`}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedProducts.includes(product.id)}
                                                onChange={() => toggleSelectProduct(product.id)}
                                                className="table-checkbox"
                                            />
                                        </td>
                                        <td>
                                            <div className="product-img-mini">
                                                {product.varPhoto || product.photo ? (
                                                    <img src={`${API_BASE_URL}${product.varPhoto || product.photo}`} alt={product.name} />
                                                ) : (
                                                    <div className="img-placeholder" />
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="p-code">
                                                {product.Code || product.code || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="product-name-only" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span className="p-name">{product.name}</span>
                                                {hiddenItems.some(h => h.product_id === product.id && h.isHidden === 1 && h.variation_id === null) && (
                                                    <span className="stock-badge out" style={{ padding: '2px 6px', fontSize: '0.7rem' }}>Hidden</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="p-desc-col">{product.description || <span style={{ color: 'var(--text-muted)' }}>No description</span>}</span>
                                        </td>
                                        <td>
                                            <div className="cat-pill-group">
                                                <span className="cat-pill main">{product.categoryName || 'Uncategorized'}</span>
                                                {product.subCategoryName && <span className="cat-pill sub">{product.subCategoryName}</span>}
                                                {product.subSubCategoryName && <span className="cat-pill specialty">{product.subSubCategoryName}</span>}
                                            </div>
                                        </td>
                                        <td>
                                            {product.typeName ? (
                                                <span className="type-badge" style={{ background: '#f5f5f7', color: '#1d1d1f', padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', border: '1px solid #e5e5e7' }}>
                                                    {product.typeName}
                                                </span>
                                            ) : (
                                                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="p-price">${parseFloat(product.Price || 0).toFixed(2)}</span>
                                        </td>
                                        <td>
                                            {product.discount_value && product.discount_is_active === 1 ? (
                                                <span className="discount-badge">
                                                    {product.discount_type === 'percentage' ? `-${product.discount_value}%` : `-$${product.discount_value}`}
                                                </span>
                                            ) : (
                                                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>
                                            )}
                                        </td>
                                        <td>
                                            {(() => {
                                                const baseStock = Number(product.StockQTY || 0);
                                                const varStock = Number(product.varStock || 0);

                                                if (varStock > 0) {
                                                    return (
                                                        <span className="stock-badge managed" title={`Total variation stock: ${varStock}`}>
                                                            Articles
                                                        </span>
                                                    );
                                                } else if (baseStock > 0) {
                                                    return (
                                                        <span className={`stock-badge ${baseStock <= 5 ? 'low' : 'ok'}`}>
                                                            ✓ {baseStock}
                                                        </span>
                                                    );
                                                } else {
                                                    return (
                                                        <span className="stock-badge out">
                                                            ✕ Out
                                                        </span>
                                                    );
                                                }
                                            })()}
                                        </td>
                                        <td>
                                            {product.seasonName ? <span className="season-badge">{product.seasonName} {product.seasonYear && `(${product.seasonYear})`}</span> : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>}
                                        </td>
                                        <td>
                                            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                                {product.Date ? new Date(product.Date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="12" className="no-results">
                                        No products found matching your criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>


            </main>

            {/* Modern Persistent Footer */}
            <footer className="persistent-footer">
                <div className="footer-content">
                    <div className="footer-section info">
                        <span className="footer-count">
                            <strong>{products.length}</strong> Products
                        </span>
                    </div>





                    <div className="footer-section main-actions">
                        <button
                            className="footer-btn refresh desktop-refresh-btn"
                            onClick={() => {
                                setSearchTerm('');
                                setFilterCategory(null);
                                setFilterSubCategory(null);
                                setFilterSpecialty(null);
                                setFilterSeason(null);
                                setFilterDateFrom('');
                                setFilterDateTo('');
                            }}
                            title="Reset filters"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="1 4 1 10 7 10"></polyline>
                                <path d="M3.51 15a9 9 0 1 0 .49-4.5"></path>
                            </svg>
                        </button>
                        <div className="action-divider"></div>
                        <button className="footer-btn new" onClick={() => {
                            if (!isQuickAddVisible) {
                                setQuickAddData({
                                    name: '',
                                    description: '',
                                    catID: '',
                                    subCatID: '',
                                    subSubID: '',
                                    Price: '',
                                    StockQTY: '',
                                    season: '',
                                    photo: null,
                                    discount_type: '',
                                    discount_value: ''
                                });
                                setQuickAddPreview(null);
                                setTimeout(() => {
                                    document.getElementById('quick-add-row')?.scrollIntoView({ behavior: 'smooth' });
                                }, 100);
                            }
                            setIsQuickAddVisible(!isQuickAddVisible);
                        }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            New
                        </button>

                        <button
                            className="footer-btn add-primary"
                            disabled={!isQuickAddVisible}
                            onClick={submitQuickAdd}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            Save
                        </button>

                        <div className="action-divider"></div>

                        <button
                            className="footer-btn edit"
                            disabled={selectedProducts.length === 0}
                            onClick={() => {
                                if (selectedProducts.length === 1) {
                                    handleOpenModal(products.find(p => p.id === selectedProducts[0]));
                                } else {
                                    setIsBulkEditModalOpen(true);
                                }
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                            {selectedProducts.length > 1 ? 'Bulk Edit' : 'Edit'}
                        </button>

                        <button
                            className="footer-btn variations-btn"
                            disabled={selectedProducts.length !== 1}
                            onClick={() => handleOpenVariations(products.find(p => p.id === selectedProducts[0]))}
                            style={{ background: 'var(--bg-white)', color: 'var(--primary)', border: '1.5px solid var(--primary-glow)' }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                <line x1="12" y1="22.08" x2="12" y2="12"></line>
                            </svg>
                            Variations
                        </button>

                        <button
                            className="footer-btn offer-btn"
                            disabled={selectedProducts.length === 0}
                            onClick={() => {
                                if (selectedProducts.length === 1) {
                                    handleOpenOfferModal(products.find(p => p.id === selectedProducts[0]));
                                } else {
                                    handleOpenOfferModal(null);
                                }
                            }}
                            style={{ background: 'var(--bg-white)', color: '#e67e22', border: '1.5px solid #e67e22' }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                                <line x1="7" y1="7" x2="7.01" y2="7"></line>
                            </svg>
                            Offers {selectedProducts.length > 1 && `(${selectedProducts.length})`}
                        </button>

                        <button
                            className="footer-btn discount-btn"
                            disabled={selectedProducts.length === 0}
                            onClick={() => {
                                if (selectedProducts.length === 1) {
                                    handleOpenDiscountModal('tableSingle');
                                } else {
                                    handleOpenDiscountModal('bulk');
                                }
                            }}
                            title={selectedProducts.length > 0 ? "Set discount for selected products" : "Select products to set discount"}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="5" x2="5" y2="19"></line>
                                <circle cx="6.5" cy="6.5" r="2.5"></circle>
                                <circle cx="17.5" cy="17.5" r="2.5"></circle>
                            </svg>
                            Discount {selectedProducts.length > 1 && `(${selectedProducts.length})`}
                        </button>

                        <button
                            className="footer-btn delete"
                            disabled={selectedProducts.length === 0}
                            onClick={handleBulkDelete}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                            Delete {selectedProducts.length > 0 && `(${selectedProducts.length})`}
                        </button>

                        <button
                            className="footer-btn offer-btn"
                            disabled={selectedProducts.length === 0}
                            onClick={handleBulkToggleVisibility}
                            style={{ background: 'var(--bg-white)', color: '#636e72', border: '1.5px solid #b2bec3' }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                                <line x1="1" y1="1" x2="23" y2="23"></line>
                            </svg>
                            Toggle Visibility
                        </button>

                        <button
                            className="footer-btn cancel"
                            disabled={selectedProducts.length === 0 && !isQuickAddVisible}
                            onClick={() => {
                                if (isQuickAddVisible) setIsQuickAddVisible(false);
                                if (selectedProducts.length > 0) setSelectedProducts([]);
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                            Cancel
                        </button>
                    </div>
                </div>
            </footer>

            {/* Edit Modal (Keeping only for editing) */}
            {isModalOpen && editingProduct && (
                <div className="modal-overlay">
                    <div className="product-modal edit-product-modal">
                        <div className="modal-header">
                            <h2>Edit Product</h2>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button type="button" className="cancel-btn" onClick={handleCloseModal} style={{ padding: '6px 16px', fontSize: '0.85rem' }}>Cancel</button>
                                <button type="submit" form="edit-product-form" className="save-btn" style={{ padding: '6px 16px', fontSize: '0.85rem' }}>Update Product</button>
                            </div>
                        </div>
                        <form id="edit-product-form" onSubmit={handleSubmit} className="product-form">
                            <div className="form-main">
                                <div className="form-left">
                                    <div className="image-upload-section" onClick={() => fileInputRef.current.click()}>
                                        {imagePreview ? (
                                            <img src={imagePreview} alt="Preview" />
                                        ) : (
                                            <div className="upload-placeholder">
                                                <span>Upload Image</span>
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                            accept="image/*"
                                        />
                                    </div>




                                </div>

                                <div className="form-right">
                                    <div className="form-row type-row">
                                        <div className="form-group">
                                            <label>Product Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>Product Type</label>
                                            <select name="type" value={formData.type} onChange={handleInputChange}>
                                                <option value="">Select Type</option>
                                                {attributeValues
                                                    .filter(av => av.attributeName?.toLowerCase() === 'type')
                                                    .map(av => <option key={av.id} value={av.id}>{av.value}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Season</label>
                                            <select name="season" value={formData.season} onChange={handleInputChange}>
                                                <option value="">Select Season</option>
                                                {seasons.map(s => <option key={s.id} value={s.id}>{s.name} ({s.year})</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows="4"
                                        />
                                    </div>
                                    <div className="form-row-3">
                                        <div className="form-group">
                                            <label>Category</label>
                                            <select
                                                name="catID"
                                                value={formData.catID}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Sub-Category</label>
                                            <select
                                                name="subCatID"
                                                value={formData.subCatID}
                                                onChange={handleInputChange}
                                                disabled={!formData.catID}
                                            >
                                                <option value="">Select Sub-Category</option>
                                                {subCategories
                                                    .filter(sc => sc.category_id === parseInt(formData.catID))
                                                    .map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Specialized (Sub-Sub)</label>
                                            <select
                                                name="subSubID"
                                                value={formData.subSubID}
                                                onChange={handleInputChange}
                                                disabled={!formData.subCatID}
                                            >
                                                <option value="">Select specialized</option>
                                                {subSubCategories
                                                    .filter(ssc => ssc.subcategory_id === parseInt(formData.subCatID))
                                                    .map(ssc => <option key={ssc.id} value={ssc.id}>{ssc.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-row" style={{ marginTop: '12px' }}>
                                        <div className="form-group">
                                            <label>Discount</label>
                                            <button
                                                type="button"
                                                className={`discount-modal-trigger ${formData.discount_type ? 'active' : ''}`}
                                                onClick={() => handleOpenDiscountModal('edit')}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                                                    <line x1="7" y1="7" x2="7.01" y2="7"></line>
                                                </svg>
                                                {formData.discount_type ? (
                                                    <span style={{ color: formData.discount_is_active ? 'inherit' : 'var(--text-muted)' }}>
                                                        {formData.discount_type === 'percentage' ? `${formData.discount_value}%` : `$${formData.discount_value}`} Off
                                                        {!formData.discount_is_active && ' (Paused)'}
                                                    </span>
                                                ) : (
                                                    <span>Configure Discount</span>
                                                )}
                                            </button>
                                        </div>
                                        <div className="form-group">
                                            <label>Size Guide Image</label>
                                            <button
                                                type="button"
                                                className="cancel-btn"
                                                onClick={() => sizeGuidePreview ? setSizeGuideViewerOpen(true) : sizeGuideEditInputRef.current.click()}
                                                style={{ width: '100%', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '0.85rem', padding: '10px' }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                    <polyline points="17 8 12 3 7 8"></polyline>
                                                    <line x1="12" y1="3" x2="12" y2="15"></line>
                                                </svg>
                                                {sizeGuidePreview ? 'Manage' : 'Size Guide'}
                                            </button>
                                            <input
                                                type="file"
                                                ref={sizeGuideEditInputRef}
                                                onChange={handleSizeGuideFileChange}
                                                style={{ display: 'none' }}
                                                accept="image/*"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row" style={{ marginTop: '12px' }}>
                                        <div className="form-group">
                                            <label>Price ($)</label>
                                            <input
                                                type="number"
                                                name="Price"
                                                step="0.01"
                                                value={formData.Price}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Stock</label>
                                            {editingProduct.varStock > 0 ? (
                                                <div className="managed-stock-badge" style={{ height: '38px', padding: '6px 12px' }}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '6px', flexShrink: 0 }}>
                                                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                                    </svg>
                                                    Managed by Var
                                                </div>
                                            ) : (
                                                <input
                                                    type="number"
                                                    name="StockQTY"
                                                    value={formData.StockQTY}
                                                    onChange={handleInputChange}
                                                    style={{ height: '38px' }}
                                                />
                                            )}
                                            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input
                                                    type="checkbox"
                                                    name="can_preorder"
                                                    checked={formData.can_preorder === 1}
                                                    onChange={(e) => {
                                                        const currentStock = (editingProduct && editingProduct.varStock > 0) ? editingProduct.varStock : parseInt(formData.StockQTY || 0);
                                                        if (e.target.checked && currentStock === 0) {
                                                            showAlertDialog('Cannot enable pre-order for an item with 0 stock.', 'Invalid Input');
                                                            return;
                                                        }
                                                        setFormData(prev => ({ ...prev, can_preorder: e.target.checked ? 1 : 0 }));
                                                    }}
                                                    id="can_preorder_cb"
                                                />
                                                <label htmlFor="can_preorder_cb" style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-main)' }}>Can Pre-order?</label>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Size Guide Viewer Modal */}
            {sizeGuideViewerOpen && sizeGuidePreview && (
                <div className="modal-overlay" style={{ zIndex: 10000 }} onClick={() => setSizeGuideViewerOpen(false)}>
                    <div className="viewer-modal" style={{ background: '#fff', padding: '24px', borderRadius: '16px', maxWidth: '90%', maxHeight: '90%', position: 'relative', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => setSizeGuideViewerOpen(false)} style={{ position: 'absolute', top: '16px', right: '16px', background: '#f5f5f7', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', cursor: 'pointer', color: '#1d1d1f' }}>✕</button>
                        <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '1.2rem', color: '#1d1d1f' }}>Size Guide</h3>
                        <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img src={sizeGuidePreview} alt="Size Guide Large" style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: '8px' }} />
                        </div>
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
                            <button type="button" onClick={() => sizeGuideEditInputRef.current.click()} style={{ padding: '8px 16px', background: '#ff4757', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Change Image</button>
                            <button type="button" onClick={() => { setFormData(prev => ({ ...prev, size_guide_image: '' })); setSizeGuidePreview(null); setSizeGuideViewerOpen(false); }} style={{ padding: '8px 16px', background: '#f5f5f7', color: '#333', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer' }}>Remove Image</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Edit Modal */}
            {isBulkEditModalOpen && (
                <div className="modal-overlay bem-overlay" onClick={() => setIsBulkEditModalOpen(false)}>
                    <div className="bem-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="bem-header">
                            <div className="bem-header-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 20h9"></path>
                                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                </svg>
                            </div>
                            <div className="bem-header-text">
                                <h2>Bulk Edit</h2>
                                <p>Leave a field on “No change” to keep its current value.</p>
                            </div>
                            <span className="bem-count">{selectedProducts.length} items</span>
                            <button type="button" className="bem-close" onClick={() => setIsBulkEditModalOpen(false)} aria-label="Close">×</button>
                        </div>

                        <div className="bem-body">
                            <div className="bem-field">
                                <label htmlFor="bem-cat">Category</label>
                                <select id="bem-cat" name="catID" value={bulkEditData.catID} onChange={handleBulkEditChange}>
                                    <option value="">No change</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="bem-row">
                                <div className="bem-field">
                                    <label htmlFor="bem-subcat">Sub-Category</label>
                                    <select id="bem-subcat" name="subCatID" value={bulkEditData.subCatID} onChange={handleBulkEditChange} disabled={!bulkEditData.catID}>
                                        <option value="">No change</option>
                                        {subCategories.filter(sc => sc.category_id === parseInt(bulkEditData.catID)).map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                                    </select>
                                </div>
                                <div className="bem-field">
                                    <label htmlFor="bem-subsub">Specialized</label>
                                    <select id="bem-subsub" name="subSubID" value={bulkEditData.subSubID} onChange={handleBulkEditChange} disabled={!bulkEditData.subCatID}>
                                        <option value="">No change</option>
                                        {subSubCategories.filter(ssc => ssc.subcategory_id === parseInt(bulkEditData.subCatID)).map(ssc => <option key={ssc.id} value={ssc.id}>{ssc.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="bem-field">
                                <label htmlFor="bem-season">Season</label>
                                <select id="bem-season" name="season" value={bulkEditData.season} onChange={handleBulkEditChange}>
                                    <option value="">No change</option>
                                    {seasons.map(s => <option key={s.id} value={s.id}>{s.name} ({s.year})</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="bem-footer">
                            <button type="button" id="bem-cancel" className="bem-btn bem-btn-ghost" onClick={() => setIsBulkEditModalOpen(false)}>Cancel</button>
                            <button type="button" id="bem-apply" className="bem-btn bem-btn-primary" onClick={submitBulkEdit}>Apply Updates</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Category Picker Modal */}
            {isCatPickerOpen && (
                <div className="modal-overlay cat-picker-overlay">
                    <div className="cat-picker-modal">
                        <div className="modal-header">
                            <h3>Select Product Categories</h3>
                        </div>
                        <div className="cat-picker-content">
                            <div className="picker-section">
                                <label>Main Category</label>
                                <div className="picker-grid">
                                    {categories.map(c => (
                                        <button
                                            key={c.id}
                                            className={`picker-btn ${parseInt(quickAddData.catID) === c.id ? 'active' : ''}`}
                                            onClick={() => handleQuickAddChange({ target: { name: 'catID', value: c.id.toString() } })}
                                        >
                                            {c.name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {quickAddData.catID && (
                                <div className="picker-section">
                                    <label>Sub-Category</label>
                                    <div className="picker-grid">
                                        {subCategories
                                            .filter(sc => sc.category_id === parseInt(quickAddData.catID))
                                            .map(sc => (
                                                <button
                                                    key={sc.id}
                                                    className={`picker-btn sub ${parseInt(quickAddData.subCatID) === sc.id ? 'active' : ''}`}
                                                    onClick={() => handleQuickAddChange({ target: { name: 'subCatID', value: sc.id.toString() } })}
                                                >
                                                    {sc.name}
                                                </button>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {quickAddData.subCatID && (
                                <div className="picker-section">
                                    <label>Specialized Category</label>
                                    <div className="picker-grid">
                                        {subSubCategories
                                            .filter(ssc => ssc.subcategory_id === parseInt(quickAddData.subCatID))
                                            .map(ssc => (
                                                <button
                                                    key={ssc.id}
                                                    className={`picker-btn specialty ${parseInt(quickAddData.subSubID) === ssc.id ? 'active' : ''}`}
                                                    onClick={() => handleQuickAddChange({ target: { name: 'subSubID', value: ssc.id.toString() } })}
                                                >
                                                    {ssc.name}
                                                </button>
                                            ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="save-btn" onClick={() => setIsCatPickerOpen(false)}>Done</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Variations Management Modal */}
            {isVariationsModalOpen && currentProductForVariations && (
                <div className="modal-overlay">
                    <div className="product-modal variations-modal">
                        <div className="modal-header">
                            <div className="header-text">
                                <h2>Product Variations</h2>
                                <p>{currentProductForVariations.name}</p>
                            </div>
                            <div className="header-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                {!editingVariationId && (
                                    <button className="save-btn-compact" onClick={submitMatrixVariations}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '6px' }}>
                                            <path d="M12 5v14M5 12h14" />
                                        </svg>
                                        Create All
                                    </button>
                                )}
                                <button className="header-save-btn" onClick={() => setIsVariationsModalOpen(false)}>
                                    Save
                                </button>
                            </div>
                        </div>

                        <div className="variations-layout">
                            {/* Left: Form */}
                            <div className="variation-editor">
                                <div className="editor-card">
                                    {editingVariationId && (
                                        <div className="editor-card-header">
                                            <h3>Edit Variation</h3>
                                        </div>
                                    )}

                                    {!editingVariationId && (
                                        <div className="matrix-setup">
                                            <div className="matrix-selectors">
                                                <div className="selector-group">
                                                    <label>X-Axis (Sizes)</label>
                                                    <div className="matrix-sections-container horizontal-flow">
                                                        {/* Standalone Section */}
                                                        <div className="matrix-section standalone">
                                                            <div className="section-pills">
                                                                {attributeValues
                                                                    .filter(av => av.attributeName?.toLowerCase() === 'size' && !av.family_id)
                                                                    .map(av => (
                                                                        <button
                                                                            key={av.id}
                                                                            className={`pill-btn ${matrixSizes.find(s => s.id === av.id) ? 'active' : ''}`}
                                                                            onClick={() => toggleMatrixSize(av)}
                                                                        >
                                                                            {av.value}
                                                                        </button>
                                                                    ))}
                                                            </div>
                                                        </div>

                                                        {/* Family Sections */}
                                                        {attributeFamilies
                                                            .filter(af => af.attributeName?.toLowerCase() === 'size')
                                                            .map(af => {
                                                                const familyValues = attributeValues.filter(av => av.family_id === af.id);
                                                                const allSelected = familyValues.every(fv => matrixSizes.find(ms => ms.id === fv.id));

                                                                return (
                                                                    <div key={af.id} className="matrix-section family">
                                                                        <div className="section-header-inline">
                                                                            <button
                                                                                className={`family-name-tag btn-action ${allSelected ? 'active' : ''}`}
                                                                                onClick={() => {
                                                                                    if (allSelected) {
                                                                                        const idsToRemove = familyValues.map(fv => fv.id);
                                                                                        setMatrixSizes(prev => prev.filter(s => !idsToRemove.includes(s.id)));
                                                                                    } else {
                                                                                        setMatrixSizes(prev => {
                                                                                            const existingIds = prev.map(s => s.id);
                                                                                            const toAdd = familyValues.filter(fv => !existingIds.includes(fv.id));
                                                                                            return [...prev, ...toAdd];
                                                                                        });
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {af.name}
                                                                                {allSelected && <span className="active-dot">●</span>}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                </div>
                                                <div className="selector-group">
                                                    <label>Y-Axis (Colors)</label>
                                                    <div className="matrix-sections-container horizontal-flow">
                                                        {/* Standalone Section */}
                                                        <div className="matrix-section standalone">
                                                            <div className="section-pills">
                                                                {attributeValues
                                                                    .filter(av => av.attributeName?.toLowerCase() === 'color' && !av.family_id)
                                                                    .map(av => (
                                                                        <button
                                                                            key={av.id}
                                                                            className={`pill-btn color ${matrixColors.find(c => c.id === av.id) ? 'active' : ''}`}
                                                                            onClick={() => toggleMatrixColor(av)}
                                                                        >
                                                                            {av.value}
                                                                        </button>
                                                                    ))}
                                                            </div>
                                                        </div>

                                                        {/* Family Sections */}
                                                        {attributeFamilies
                                                            .filter(af => af.attributeName?.toLowerCase() === 'color')
                                                            .map(af => {
                                                                const familyValues = attributeValues.filter(av => av.family_id === af.id);
                                                                const allSelected = familyValues.every(fv => matrixColors.find(ms => ms.id === fv.id));

                                                                return (
                                                                    <div key={af.id} className="matrix-section family">
                                                                        <div className="section-header-inline">
                                                                            <button
                                                                                className={`family-name-tag btn-action ${allSelected ? 'active' : ''}`}
                                                                                onClick={() => {
                                                                                    if (allSelected) {
                                                                                        const idsToRemove = familyValues.map(fv => fv.id);
                                                                                        setMatrixColors(prev => prev.filter(c => !idsToRemove.includes(c.id)));
                                                                                    } else {
                                                                                        setMatrixColors(prev => {
                                                                                            const existingIds = prev.map(c => c.id);
                                                                                            const toAdd = familyValues.filter(fv => !existingIds.includes(fv.id));
                                                                                            return [...prev, ...toAdd];
                                                                                        });
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {af.name}
                                                                                {allSelected && <span className="active-dot">●</span>}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                </div>
                                            </div>

                                            {(matrixSizes.length > 0 || matrixColors.length > 0) && (
                                                <div className="matrix-grid-wrapper">
                                                    <table className="matrix-table-grid">
                                                        <thead>
                                                            <tr>
                                                                <th style={{ width: '50px' }}>Main</th>
                                                                <th>Photo</th>
                                                                <th>{matrixColors.length > 0 ? 'Color' : ''} \ {matrixSizes.length > 0 ? 'Size' : ''}</th>
                                                                {(matrixSizes.length > 0 ? matrixSizes : [{ id: '', value: 'None' }]).map(sizeObj => (
                                                                    <th key={sizeObj.id || 'none'}>{sizeObj.value || '—'}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(matrixColors.length > 0 ? matrixColors : [{ id: '', value: 'None' }]).map((colorObj, cIdx) => (
                                                                <tr key={colorObj.id || 'none'}>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="main-color-select"
                                                                            checked={selectedMainColorId === colorObj.id}
                                                                            onChange={() => setSelectedMainColorId(colorObj.id)}
                                                                        />
                                                                    </td>
                                                                    <td className="matrix-color-photo-cell">
                                                                        <div className="matrix-color-photo-container">
                                                                            <div
                                                                                className="matrix-color-photo-upload"
                                                                                onClick={() => document.getElementById(`color-photo-input-${colorObj.id || 'none'}`).click()}
                                                                            >
                                                                                {matrixColorPreviews[colorObj.id] ? (
                                                                                    <img src={matrixColorPreviews[colorObj.id]} alt="" />
                                                                                ) : (
                                                                                    <span className="upload-icon-mini">+</span>
                                                                                )}
                                                                                <input
                                                                                    type="file"
                                                                                    id={`color-photo-input-${colorObj.id || 'none'}`}
                                                                                    style={{ display: 'none' }}
                                                                                    accept="image/*"
                                                                                    onChange={(e) => handleColorPhotoChange(colorObj.id, e.target.files[0])}
                                                                                />
                                                                            </div>
                                                                            {matrixColorPreviews[colorObj.id] && (
                                                                                <button
                                                                                    className="color-photo-clear"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setMatrixColorPhotos(prev => {
                                                                                            const next = { ...prev };
                                                                                            delete next[colorObj.id];
                                                                                            return next;
                                                                                        });
                                                                                        setMatrixColorPreviews(prev => {
                                                                                            const next = { ...prev };
                                                                                            delete next[colorObj.id];
                                                                                            return next;
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="color-label">{colorObj.value === 'None' ? '—' : colorObj.value}</td>
                                                                    {(matrixSizes.length > 0 ? matrixSizes : [{ id: '', value: 'None' }]).map((sizeObj, sIdx) => {
                                                                        return (
                                                                            <td key={sizeObj.id || 'none'}>
                                                                                <input
                                                                                    type="number"
                                                                                    className="matrix-input"
                                                                                    placeholder="Stock"
                                                                                    data-row={cIdx}
                                                                                    data-col={sIdx}
                                                                                    value={matrixStock[`${sizeObj.id}:${colorObj.id}`] || ''}
                                                                                    onChange={(e) => handleMatrixStockChange(sizeObj.id, colorObj.id, e.target.value)}
                                                                                    onKeyDown={(e) => handleMatrixKeyDown(e, cIdx, sIdx)}
                                                                                />
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right: List */}
                            <div className="variation-viewer">
                                <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <h3>Existing Variations <span className="variation-count">{variations.length}</span></h3>
                                        <button
                                            type="button"
                                            className="pill-btn size-chart-btn"
                                            onClick={() => {
                                                setSizeGuideModalPreview(currentProductForVariations.size_guide_image ? `${API_BASE_URL}${currentProductForVariations.size_guide_image}` : null);
                                                setSizeGuideFile(null);
                                                setIsSizeChartModalOpen(true);
                                            }}
                                            style={{ background: 'linear-gradient(135deg, #eb5e28 0%, #d84a16 100%)', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', padding: '6px 14px', borderRadius: '30px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 10px rgba(235, 94, 40, 0.2)', transition: 'all 0.2s' }}
                                        >
                                            Configure Size Guide Image
                                        </button>
                                    </div>

                                    {variations.length > 0 && Array.from(new Set(variations.map(v => v.Color).filter(c => c))).length > 0 && (
                                        <div className="main-color-picker-mini">
                                            <span>Display Color:</span>
                                            {(() => {
                                                const colorIds = Array.from(new Set(variations.map(v => v.Color).filter(c => c)));
                                                const activeColor = colorIds.find(colorId => variations.some(v => v.Color === colorId && v.isMain));
                                                return (
                                                    <button
                                                        className={`pill-btn color-mini ${activeColor ? 'active' : ''}`}
                                                        onClick={() => setShowColorDialog(true)}
                                                    >
                                                        {activeColor ? getAttributeName(activeColor) : 'Select Color'}
                                                        <span style={{ marginLeft: '6px', opacity: 0.7 }}>▾</span>
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>

                                {/* Color Select Dialog */}
                                {showColorDialog && (
                                    <div className="color-dialog-overlay" onClick={() => setShowColorDialog(false)}>
                                        <div className="color-dialog" onClick={e => e.stopPropagation()}>
                                            <div className="color-dialog-header">
                                                <span>Select Display Color</span>
                                                <button className="color-dialog-close" onClick={() => setShowColorDialog(false)}>✕</button>
                                            </div>
                                            <div className="color-dialog-body">
                                                {Array.from(new Set(variations.map(v => v.Color).filter(c => c))).map(colorId => {
                                                    const isMain = variations.some(v => v.Color === colorId && v.isMain);
                                                    return (
                                                        <button
                                                            key={colorId}
                                                            onClick={() => { handleSetMainColor(colorId); setShowColorDialog(false); }}
                                                            className={`color-dialog-option ${isMain ? 'active' : ''}`}
                                                        >
                                                            <span className="color-dialog-check">{isMain ? '✓' : ''}</span>
                                                            {getAttributeName(colorId)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="variation-table-wrapper">
                                    {variations.length > 0 ? (
                                        <table className="variation-table">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Photo</th>
                                                    <th>Label / Name</th>
                                                    <th>Size</th>
                                                    <th>Color</th>
                                                    <th>Type</th>
                                                    <th>Stock</th>
                                                    <th>Discount</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[...variations].sort((a, b) => {
                                                    const sizeA = getAttributeName(a.Size);
                                                    const sizeB = getAttributeName(b.Size);
                                                    if (sizeA !== sizeB) return sizeA.localeCompare(sizeB);
                                                    const colorA = getAttributeName(a.Color);
                                                    const colorB = getAttributeName(b.Color);
                                                    return colorA.localeCompare(colorB);
                                                }).map(v => (
                                                    <tr key={v.varID} className={editingVariationId === v.varID ? 'editing' : ''}>
                                                        {editingVariationId === v.varID ? (
                                                            <>
                                                                <td style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-muted)' }}>#{v.varID}</td>
                                                                <td>
                                                                    <div className="var-img-mini" onClick={() => varFileInputRef.current.click()} style={{ cursor: 'pointer', marginBottom: '4px' }}>
                                                                        {variationPreview ? <img src={variationPreview} alt="" /> : (v.photo ? <img src={`${API_BASE_URL}${v.photo}`} alt="" /> : <div className="img-placeholder">+</div>)}
                                                                        <input type="file" ref={varFileInputRef} onChange={handleVariationFileChange} style={{ display: 'none' }} accept="image/*" />
                                                                    </div>
                                                                    {variationFormData.ColorID && variationFormData.photo && (
                                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={applyImageToAllColor}
                                                                                onChange={(e) => setApplyImageToAllColor(e.target.checked)}
                                                                            />
                                                                            Apply to {variationFormData.Color}
                                                                        </label>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        className="inline-input"
                                                                        name="Name"
                                                                        value={variationFormData.Name}
                                                                        onChange={handleVariationInputChange}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <button className="attr-trigger-mini" onClick={() => setAttrPickerOpen('Size')}>
                                                                        {variationFormData.Size || '—'}
                                                                    </button>
                                                                </td>
                                                                <td>
                                                                    <button className="attr-trigger-mini" onClick={() => setAttrPickerOpen('Color')}>
                                                                        {variationFormData.Color || '—'}
                                                                    </button>
                                                                </td>
                                                                <td>
                                                                    <button className="attr-trigger-mini" onClick={() => setAttrPickerOpen('Type')}>
                                                                        {variationFormData.Type || '—'}
                                                                    </button>
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        className="inline-input stock"
                                                                        name="Qty"
                                                                        value={variationFormData.Qty}
                                                                        onChange={handleVariationInputChange}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className={`attr-trigger-mini ${variationFormData.discount_type ? 'active' : ''}`}
                                                                        onClick={() => handleOpenDiscountModal('variationEdit')}
                                                                        style={{ width: '100%', fontSize: '0.7rem' }}
                                                                    >
                                                                        {variationFormData.discount_type ? (
                                                                            <span>{variationFormData.discount_type === 'percentage' ? `${variationFormData.discount_value}%` : `$${variationFormData.discount_value}`} {variationFormData.discount_is_active ? '' : '(P)'}</span>
                                                                        ) : 'Set'}
                                                                    </button>
                                                                </td>
                                                                <td>
                                                                    <div className="var-inline-actions">
                                                                        <button onClick={submitVariation} className="inline-save-btn">Save</button>
                                                                        <button onClick={() => setEditingVariationId(null)} className="inline-cancel-btn">Cancel</button>
                                                                    </div>
                                                                </td>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <td style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-muted)' }}>#{v.varID}</td>
                                                                <td>
                                                                    <div className="var-img-mini">
                                                                        {v.photo ? <img src={`${API_BASE_URL}${v.photo}`} alt="" /> : <div className="img-placeholder" />}
                                                                    </div>
                                                                </td>
                                                                <td className="var-name-cell">{v.Name || 'Unnamed'}</td>
                                                                <td>{getAttributeName(v.Size)}</td>
                                                                <td>{getAttributeName(v.Color)}</td>
                                                                <td>{getAttributeName(v.Type)}</td>
                                                                <td>
                                                                    <span className={`stock-badge ${v.Qty === 0 ? 'out' : v.Qty <= 5 ? 'low' : 'ok'}`}>
                                                                        {v.Qty}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {v.discount_value && v.discount_is_active === 1 ? (
                                                                        <span className="discount-badge mini">
                                                                            {v.discount_type === 'percentage' ? `${v.discount_value}%` : `$${v.discount_value}`}
                                                                        </span>
                                                                    ) : <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>}
                                                                </td>
                                                                <td>
                                                                    <div className="var-row-actions">
                                                                        {deletingVariationId === v.varID ? (
                                                                            <div className="var-confirm-actions">
                                                                                <button
                                                                                    onClick={() => handleDeleteVariation(v.varID)}
                                                                                    className="confirm-btn-mini delete"
                                                                                    title="Confirm Delete"
                                                                                >
                                                                                    Confirm
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => setDeletingVariationId(null)}
                                                                                    className="confirm-btn-mini cancel"
                                                                                    title="Cancel"
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                <button onClick={() => handleEditVariation(v)} className="action-icon-btn edit" title="Edit">
                                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                                                                </button>
                                                                                <button onClick={() => setDeletingVariationId(v.varID)} className="action-icon-btn delete" title="Delete">
                                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="empty-variations">
                                            <div className="empty-icon">📂</div>
                                            <p>No variations found for this product.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isSizeChartModalOpen && currentProductForVariations && (
                <div className="modal-overlay size-scale-overlay" style={{ zIndex: 9999, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '10%' }}>
                    <style>{`
                        @media (max-width: 600px) {
                            .size-scale-modal {
                                padding: 18px !important;
                                width: 92% !important;
                                border-radius: 12px !important;
                            }
                            .size-scale-modal .modal-header {
                                gap: 10px !important;
                                margin-bottom: 16px !important;
                                padding-bottom: 12px !important;
                            }
                            .size-scale-modal .header-text h2 {
                                font-size: 1.1rem !important;
                            }
                            .size-scale-modal .header-text p {
                                font-size: 0.78rem !important;
                            }
                            .size-scale-modal th, .size-scale-modal td {
                                padding: 8px 4px !important;
                            }
                            .size-scale-modal input {
                                width: 52px !important;
                                padding: 4px 6px !important;
                                font-size: 0.8rem !important;
                            }
                            .size-scale-modal .modal-footer {
                                margin-top: 14px !important;
                                padding-top: 12px !important;
                                gap: 8px !important;
                            }
                            .size-scale-modal .modal-footer button {
                                padding: 8px 16px !important;
                                font-size: 0.8rem !important;
                                border-radius: 8px !important;
                            }
                        }
                    `}</style>
                    <div className="product-modal size-scale-modal" style={{ maxWidth: '500px', width: '90%', borderRadius: '16px', background: '#ffffff', boxShadow: '0 20px 60px rgba(0,0,0,0.12)', border: '1px solid #e5e5e7', padding: '28px', boxSizing: 'border-box' }}>
                        <div className="modal-header" style={{ borderBottom: '1px solid #f5f5f7', paddingBottom: '18px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(235, 94, 40, 0.08)', color: '#eb5e28', fontSize: '1.2rem', flexShrink: 0 }}>
                                    📏
                                </div>
                                <div className="header-text">
                                    <h2 style={{ fontSize: '1.15rem', fontWeight: '700', color: '#1d1d1f', margin: 0, letterSpacing: '-0.3px' }}>
                                        Product Size Guide Image
                                    </h2>
                                    <p style={{ fontSize: '0.8rem', color: '#86868b', marginTop: '4px', margin: 0, lineHeight: '1.4' }}>
                                        Upload a size chart image for <strong>{currentProductForVariations.name}</strong>.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="size-scale-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
                            <div
                                className="image-upload-section"
                                onClick={() => sizeGuideInputRef.current.click()}
                                style={{
                                    width: '100%',
                                    height: '240px',
                                    minHeight: '240px',
                                    borderRadius: '12px',
                                    border: '2px dashed #d2d2d7',
                                    background: '#f5f5f7',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    transition: 'border-color 0.2s'
                                }}
                            >
                                {sizeGuideModalPreview ? (
                                    <img
                                        src={sizeGuideModalPreview}
                                        alt="Size Guide Preview"
                                        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                    />
                                ) : (
                                    <div className="upload-placeholder" style={{ textAlign: 'center', color: '#86868b', padding: '20px' }}>
                                        <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📤</div>
                                        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1d1d1f' }}>Upload Size Chart Image</span>
                                        <p style={{ fontSize: '0.75rem', margin: '4px 0 0', color: '#86868b' }}>Support JPG, PNG, WEBP standard charts</p>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={sizeGuideInputRef}
                                    onChange={handleSizeGuideModalFileChange}
                                    style={{ display: 'none' }}
                                    accept="image/*"
                                />
                            </div>

                            {sizeGuideModalPreview && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSizeGuideFile(null);
                                        setSizeGuideModalPreview(null);
                                    }}
                                    style={{
                                        background: 'rgba(230, 57, 70, 0.08)',
                                        color: '#e63946',
                                        border: 'none',
                                        padding: '6px 14px',
                                        borderRadius: '20px',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s'
                                    }}
                                >
                                    🗑️ Remove Size Guide Chart
                                </button>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: '16px', marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                className="cancel-btn"
                                onClick={() => setIsSizeChartModalOpen(false)}
                                style={{ background: '#f5f5f7', color: '#1d1d1f', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s' }}
                            >
                                Cancel
                            </button>
                            <button
                                className="save-btn"
                                onClick={handleSaveSizeGuideImage}
                                style={{ background: 'linear-gradient(135deg, #eb5e28 0%, #d84a16 100%)', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '12px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(235, 94, 40, 0.3)', transition: 'transform 0.2s' }}
                            >
                                Save Size Guide
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isDeleteModalOpen && (
                <div className="modal-overlay delete-modal-overlay">
                    <div className="delete-modal">
                        <div className="delete-icon-box">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                        </div>
                        <h2>Confirm Delete</h2>
                        <p>Are you sure you want to delete <strong>{selectedProducts.length}</strong> selected products? This action cannot be undone.</p>
                        <div className="delete-modal-actions">
                            <button className="delete-cancel" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
                            <button className="delete-confirm" onClick={confirmDelete}>Delete Products</button>
                        </div>
                    </div>
                </div>
            )}

            {isHideModalOpen && (
                <div className="modal-overlay delete-modal-overlay">
                    <div className="delete-modal">
                        <div className="delete-icon-box" style={{ background: 'rgba(99, 110, 114, 0.1)', color: '#636e72' }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                                <line x1="1" y1="1" x2="23" y2="23"></line>
                            </svg>
                        </div>
                        <h2>Confirm Visibility Toggle</h2>
                        <p>Are you sure you want to toggle visibility for <strong>{selectedProducts.length}</strong> selected products?</p>
                        <div className="delete-modal-actions">
                            <button className="delete-cancel" onClick={() => setIsHideModalOpen(false)}>Cancel</button>
                            <button className="delete-confirm" style={{ background: '#636e72' }} onClick={confirmToggleVisibility}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}
            {/* ── Attribute Value Picker Dialog ── */}
            {attrPickerOpen && (
                <div className="modal-overlay attr-picker-overlay" onClick={() => setAttrPickerOpen(null)}>
                    <div className="attr-picker-dialog" onClick={e => e.stopPropagation()}>
                        <div className="attr-picker-header">
                            <div className="attr-picker-title">
                                <span className="attr-picker-icon">
                                    {attrPickerOpen === 'Size' && '📐'}
                                    {attrPickerOpen === 'Color' && '🎨'}
                                    {attrPickerOpen === 'Type' && '🏷️'}
                                </span>
                                <h3>Select {attrPickerOpen}</h3>
                            </div>
                        </div>

                        <div className="attr-picker-body">
                            {attributeValues.filter(av => av.attributeName?.toLowerCase() === attrPickerOpen.toLowerCase()).length === 0 ? (
                                <div className="attr-picker-empty">
                                    <span>No values defined for <strong>{attrPickerOpen}</strong>.</span>
                                    <p>Add values from the Definitions page first.</p>
                                </div>
                            ) : (
                                <div className="attr-picker-grid">
                                    {attributeValues
                                        .filter(av => av.attributeName?.toLowerCase() === attrPickerOpen.toLowerCase())
                                        .map(av => (
                                            <button
                                                key={av.id}
                                                type="button"
                                                className={`attr-option-btn ${variationFormData[attrPickerOpen + 'ID'] === av.id ? 'active' : ''}`}
                                                onClick={() => {
                                                    setVariationFormData(p => ({
                                                        ...p,
                                                        [attrPickerOpen]: av.value,
                                                        [attrPickerOpen + 'ID']: av.id
                                                    }));
                                                    setAttrPickerOpen(null);
                                                }}
                                            >
                                                {av.value}
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>

                        <div className="attr-picker-footer">
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => {
                                    setVariationFormData(p => ({
                                        ...p,
                                        [attrPickerOpen]: '',
                                        [attrPickerOpen + 'ID']: ''
                                    }));
                                    setAttrPickerOpen(null);
                                }}
                            >
                                Clear
                            </button>
                            <button type="button" className="save-btn" onClick={() => setAttrPickerOpen(null)}>Done</button>
                        </div>
                    </div>
                </div>
            )}
            {alertDialog.isOpen && (
                <div className="modal-overlay alert-modal-overlay">
                    <div className="delete-modal alert-modal">
                        <div className="delete-icon-box alert-icon-box">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <h2>{alertDialog.title}</h2>
                        <p>{alertDialog.message}</p>
                        <div className="delete-modal-actions">
                            <button className="delete-confirm alert-confirm" onClick={() => setAlertDialog({ ...alertDialog, isOpen: false })}>Got it</button>
                        </div>
                    </div>
                </div>
            )}
            {isDiscountModalOpen && (() => {
                const isBulkDiscount = discountContext === 'bulk' || discountContext === 'bulkEdit';
                const singleName = discountContext === 'tableSingle' ? products.find(p => p.id === selectedProducts[0])?.name : null;
                const suffix = discountFormData.discount_type === 'percentage' ? '%' : '$';
                const showRemove = discountFormData.discount_id || isBulkDiscount || discountContext === 'tableSingle';
                return (
                    <div className="modal-overlay discount-modal-overlay bem-overlay" onClick={() => setIsDiscountModalOpen(false)}>
                        <div className="bem-modal bdm-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="bem-header">
                                <div className="bem-header-icon bdm-header-icon">%</div>
                                <div className="bem-header-text">
                                    <h2>{isBulkDiscount ? 'Bulk Discount' : 'Configure Discount'}</h2>
                                    <p title={singleName || undefined}>
                                        {isBulkDiscount
                                            ? 'Set promotion details once for all selected products'
                                            : singleName || 'Set promotion details for this product'}
                                    </p>
                                </div>
                                {isBulkDiscount && <span className="bem-count bdm-count">{selectedProducts.length} items</span>}
                                <button type="button" className="bem-close" onClick={() => setIsDiscountModalOpen(false)} aria-label="Close">×</button>
                            </div>

                            <div className="bem-body">
                                <div className="bem-section-title">Discount type</div>
                                <div className="bdm-segment" role="tablist">
                                    {[
                                        { key: '', label: 'None' },
                                        { key: 'percentage', label: 'Percentage' },
                                        { key: 'fixed', label: 'Fixed amount' }
                                    ].map(opt => (
                                        <button
                                            key={opt.key || 'none'}
                                            type="button"
                                            id={`bdm-type-${opt.key || 'none'}`}
                                            className={`bdm-segment-btn ${discountFormData.discount_type === opt.key ? 'active' : ''}`}
                                            onClick={() => setDiscountFormData(p => ({ ...p, discount_type: opt.key }))}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>

                                {discountFormData.discount_type ? (
                                    <>
                                        <div className="bem-section-title">Value</div>
                                        <div className="bdm-value">
                                            <input
                                                id="bdm-value"
                                                type="number"
                                                min="0"
                                                name="discount_value"
                                                value={discountFormData.discount_value}
                                                onChange={handleDiscountInputChange}
                                                placeholder="0"
                                                autoFocus
                                            />
                                            <span className="bdm-value-suffix">{suffix}</span>
                                        </div>

                                        <div className="bem-section-title">Schedule <span className="bdm-optional">optional</span></div>
                                        <div className="bem-row">
                                            <div className="bem-field">
                                                <label htmlFor="bdm-start">Start date</label>
                                                <input id="bdm-start" className="bdm-input" type="date" name="discount_start_date" value={discountFormData.discount_start_date} onChange={handleDiscountInputChange} />
                                            </div>
                                            <div className="bem-field">
                                                <label htmlFor="bdm-end">End date</label>
                                                <input id="bdm-end" className="bdm-input" type="date" name="discount_end_date" value={discountFormData.discount_end_date} onChange={handleDiscountInputChange} />
                                            </div>
                                        </div>

                                        <div className="bdm-status">
                                            <div>
                                                <strong>{discountFormData.discount_is_active ? 'Active' : 'Deactivated'}</strong>
                                                <small>{discountFormData.discount_is_active ? 'Discount is applied in the shop' : 'Saved but not applied'}</small>
                                            </div>
                                            <button
                                                type="button"
                                                id="bdm-status-toggle"
                                                className={`bdm-switch ${discountFormData.discount_is_active ? 'on' : ''}`}
                                                onClick={handleToggleDiscountStatus}
                                                aria-pressed={!!discountFormData.discount_is_active}
                                                aria-label="Toggle discount status"
                                            >
                                                <span></span>
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <p className="bdm-empty">Choose a discount type to set its value and schedule.</p>
                                )}

                                {isBulkDiscount && (
                                    <div className="bdm-note">
                                        Applies to all <strong>{selectedProducts.length}</strong> selected products and replaces their current discount.
                                    </div>
                                )}
                            </div>

                            <div className="bem-footer">
                                {showRemove && (
                                    <button type="button" id="bdm-remove" className="bem-btn bdm-btn-danger" onClick={handleDeleteDiscountDirect}>
                                        {isBulkDiscount ? 'Remove all' : 'Delete'}
                                    </button>
                                )}
                                <button type="button" id="bdm-cancel" className="bem-btn bem-btn-ghost" onClick={() => setIsDiscountModalOpen(false)}>Cancel</button>
                                <button type="button" id="bdm-apply" className="bem-btn bem-btn-primary" onClick={handleSaveDiscount}>
                                    {isBulkDiscount ? `Apply to ${selectedProducts.length}` : 'Apply discount'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
            {isDeactivateConfirmOpen && (
                <div className="modal-overlay confirm-modal-overlay">
                    <div className="confirm-modal">
                        <div className="confirm-icon">⚠️</div>
                        <h2>Deactivate Discount?</h2>
                        <p>Are you sure you want to deactivate this discount? It will no longer be applied, but will remain saved for future use.</p>
                        <div className="confirm-footer">
                            <button className="cancel-btn" onClick={() => setIsDeactivateConfirmOpen(false)}>Keep Active</button>
                            <button className="confirm-btn" onClick={confirmDeactivation}>Yes, Deactivate</button>
                        </div>
                    </div>
                </div>
            )}
            {isDiscountDeleteConfirmOpen && (
                <div className="modal-overlay confirm-modal-overlay">
                    <div className="confirm-modal delete-style">
                        <div className="confirm-icon" style={{ color: '#ff4757', background: 'rgba(255, 71, 87, 0.1)', padding: '15px', borderRadius: '50%', marginBottom: '20px' }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </div>
                        <h2>{discountContext === 'bulk' || discountContext === 'bulkEdit' ? 'Remove Discounts?' : 'Delete Discount?'}</h2>
                        <p>{discountContext === 'bulk' || discountContext === 'bulkEdit'
                            ? `Are you sure you want to remove discounts from all ${selectedProducts.length} selected products? This action cannot be undone.`
                            : 'Are you sure you want to delete this discount? This action will permanently remove it from the database and cannot be undone.'}
                        </p>
                        <div className="confirm-footer">
                            <button className="cancel-btn" onClick={() => setIsDiscountDeleteConfirmOpen(false)}>No, Keep it</button>
                            <button className="confirm-btn" style={{ background: '#ff4757', color: '#fff', border: 'none' }} onClick={confirmDiscountDelete}>Yes, Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Offer Management Modal */}
            {isOfferModalOpen && (
                <div className="modal-overlay offer-modal-overlay">
                    <div className="offer-modal">
                        <div className="modal-header offer-header">
                            <div className="header-text">
                                <h2>Manage Offer</h2>
                                <p>{offerFormData.productID === 'multiple' ? `Multiple Products (${selectedProducts.length} selected)` : products.find(p => p.id === offerFormData.productID)?.name}</p>
                            </div>
                        </div>
                        <form onSubmit={handleSaveOffer} className="offer-form">
                            <div className="offer-form-body">
                                <div className="offer-section">
                                    <div className="offer-section-title">Promotion Details</div>
                                    <div className="offer-grid">
                                        <div className="form-group">
                                            <label>Offer Quantity</label>
                                            <input
                                                type="number"
                                                value={offerFormData.offer_qty}
                                                onChange={(e) => setOfferFormData({ ...offerFormData, offer_qty: e.target.value })}
                                                required
                                            />
                                            <small className="field-hint">Minimum purchase quantity</small>
                                        </div>
                                        <div className="form-group">
                                            <label>Offer Price</label>
                                            <div className="price-input-wrapper">
                                                <span className="currency-symbol">$</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={offerFormData.offer_price}
                                                    onChange={(e) => setOfferFormData({ ...offerFormData, offer_price: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <small className="field-hint">Price per item</small>
                                        </div>
                                    </div>
                                </div>

                                <div className="offer-section">
                                    <div className="offer-section-title">Validity Period</div>
                                    <div className="offer-grid">
                                        <div className="form-group">
                                            <label>Start Date</label>
                                            <input
                                                type="date"
                                                className="date-input"
                                                value={offerFormData.from_date}
                                                onChange={(e) => setOfferFormData({ ...offerFormData, from_date: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>End Date</label>
                                            <input
                                                type="date"
                                                className="date-input"
                                                value={offerFormData.to_date}
                                                onChange={(e) => setOfferFormData({ ...offerFormData, to_date: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="offer-status-row">
                                    <div className="status-info">
                                        <span className="status-label">Active Promotion</span>
                                        <p className="status-desc">Enable this offer for customers immediately</p>
                                    </div>
                                    <label className="simple-switch">
                                        <input
                                            type="checkbox"
                                            checked={offerFormData.active === 1}
                                            onChange={handleOfferStatusToggle}
                                        />
                                        <span className="switch-slider"></span>
                                    </label>
                                </div>
                            </div>

                            <div className="modal-footer offer-footer">
                                {editingOfferId && (
                                    <button type="button" className="delete-offer-link" onClick={handleDeleteOffer}>
                                        Delete this offer
                                    </button>
                                )}
                                <div className="footer-actions">
                                    <button type="button" className="offer-cancel-btn" onClick={() => setIsOfferModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="offer-save-btn">
                                        {editingOfferId ? 'Save Changes' : 'Create Offer'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isOfferStatusConfirmOpen && (
                <div className="modal-overlay confirm-modal-overlay">
                    <div className="confirm-modal">
                        <div className="confirm-icon" style={{ color: pendingOfferStatus === 1 ? '#10b981' : '#f59e0b' }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <h2>{pendingOfferStatus === 1 ? 'Activate Promotion?' : 'Deactivate Promotion?'}</h2>
                        <p>
                            {pendingOfferStatus === 1
                                ? 'This will make the offer immediately visible and available to all customers.'
                                : 'This will hide the offer from the store. You can re-activate it at any time.'}
                        </p>
                        <div className="confirm-footer">
                            <button className="cancel-btn" onClick={() => setIsOfferStatusConfirmOpen(false)}>Cancel</button>
                            <button
                                className="confirm-btn"
                                style={{ background: pendingOfferStatus === 1 ? '#10b981' : '#f59e0b', color: '#fff', border: 'none' }}
                                onClick={confirmOfferStatusChange}
                            >
                                {pendingOfferStatus === 1 ? 'Yes, Activate' : 'Yes, Deactivate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Products;

